import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import { usePageCustomizer, CustomizeButton, CustomizerPanel, PageWidget } from '../../components/PageCustomizer';
import { useToast } from '../../context/ToastContext';
import { useLicense } from '../../context/LicenseContext';
import {
  generateVehicleDetections, generateHourlyPeopleCounts, heatmapColor, generateHeatmap,
  VEHICLE_TYPE_LABELS, VehicleDetection,
} from './AnalyticsService';

const CARPARK_WIDGETS: PageWidget[] = [
  { id:'counting',   label:'Vehicle Counting', icon:'🚗', defaultVisible:true },
  { id:'analytics',  label:'Analytics & Reports', icon:'📊', defaultVisible:true },
  { id:'alerts',     label:'Overstay Alerts',  icon:'⚠️', defaultVisible:true },
  { id:'vehicle-ai', label:'Vehicle AI',        icon:'🤖', defaultVisible:true },
  { id:'overview',   label:'Overview',          icon:'🏗️', defaultVisible:true },
  { id:'events',     label:'LPR Events',        icon:'📋', defaultVisible:true },
  { id:'zones',      label:'Zones',             icon:'🗺️', defaultVisible:true },
  { id:'bookings',   label:'Bookings',          icon:'📅', defaultVisible:true },
];

// ─── Vehicle Counting Types ───────────────────────────────────────────────────

interface ParkingLevel {
  id: string;
  name: string;
  capacity: number;
  entryCameraId: string;
  entryCameraLabel: string;
  exitCameraId: string;
  exitCameraLabel: string;
  countIn: number;
  countOut: number;
  lastReset: string;
}

interface CountEvent {
  id: number;
  levelId: string;
  levelName: string;
  direction: 'entry' | 'exit';
  time: string;
  source: 'camera' | 'manual';
}

const SEED_LEVELS: ParkingLevel[] = [
  { id:'L-B2', name:'Level B2',   capacity:120, entryCameraId:'CAM-B2-ENTRY', entryCameraLabel:'B2 Entry Ramp',   exitCameraId:'CAM-B2-EXIT', exitCameraLabel:'B2 Exit Ramp',   countIn:87,  countOut:14, lastReset:'06:00' },
  { id:'L-B1', name:'Level B1',   capacity:80,  entryCameraId:'CAM-B1-ENTRY', entryCameraLabel:'B1 Entry Ramp',   exitCameraId:'CAM-B1-EXIT', exitCameraLabel:'B1 Exit Ramp',   countIn:71,  countOut:22, lastReset:'06:00' },
  { id:'L-G',  name:'Ground',     capacity:40,  entryCameraId:'CAM-G-ENTRY',  entryCameraLabel:'Ground Entry',    exitCameraId:'CAM-G-EXIT',  exitCameraLabel:'Ground Exit',    countIn:38,  countOut:18, lastReset:'06:00' },
  { id:'L-R',  name:'Roof (P6)',  capacity:200, entryCameraId:'CAM-R-ENTRY',  entryCameraLabel:'Roof Entry Ramp', exitCameraId:'CAM-R-EXIT',  exitCameraLabel:'Roof Exit Ramp', countIn:12,  countOut:4,  lastReset:'06:00' },
];

const SEED_COUNT_EVENTS: CountEvent[] = [
  { id:1, levelId:'L-B2', levelName:'Level B2', direction:'entry', time:'08:41:08', source:'camera' },
  { id:2, levelId:'L-B1', levelName:'Level B1', direction:'entry', time:'08:39:44', source:'camera' },
  { id:3, levelId:'L-B2', levelName:'Level B2', direction:'exit',  time:'08:38:12', source:'camera' },
  { id:4, levelId:'L-G',  levelName:'Ground',   direction:'entry', time:'08:35:55', source:'camera' },
  { id:5, levelId:'L-B1', levelName:'Level B1', direction:'exit',  time:'08:31:02', source:'camera' },
  { id:6, levelId:'L-R',  levelName:'Roof (P6)',direction:'entry', time:'08:28:19', source:'camera' },
  { id:7, levelId:'L-B2', levelName:'Level B2', direction:'entry', time:'08:22:44', source:'camera' },
  { id:8, levelId:'L-G',  levelName:'Ground',   direction:'exit',  time:'08:18:33', source:'camera' },
];

let _evtSeq = 100;
function nextEvtId() { return ++_evtSeq; }

function nowTime(): string {
  return new Date().toLocaleTimeString('en-AU', { hour:'2-digit', minute:'2-digit', second:'2-digit' });
}

// ─── Vehicle Counter Card ─────────────────────────────────────────────────────

function LevelCounterCard({ level, onEntry, onExit, onReset }: {
  level: ParkingLevel;
  onEntry: (id: string) => void;
  onExit: (id: string) => void;
  onReset: (id: string) => void;
}) {
  const occupied = Math.max(0, level.countIn - level.countOut);
  const available = Math.max(0, level.capacity - occupied);
  const pct = Math.min(100, Math.round((occupied / level.capacity) * 100));
  const color = pct >= 95 ? '#ef4444' : pct >= 80 ? '#f59e0b' : '#22c55e';
  const [simulating, setSimulating] = useState(false);

  function simulateDetect(dir: 'entry' | 'exit') {
    setSimulating(true);
    setTimeout(() => {
      if (dir === 'entry') onEntry(level.id);
      else onExit(level.id);
      setSimulating(false);
    }, 600);
  }

  return (
    <div style={{ background:'#0d0d0d', border:`1px solid ${pct>=95?'rgba(239,68,68,0.3)':pct>=80?'rgba(245,158,11,0.2)':'rgba(255,255,255,0.07)'}`, borderRadius:'5px', overflow:'hidden' }}>
      {/* Colour bar */}
      <div style={{ height:'4px', background:color }} />
      <div style={{ padding:'16px' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'14px' }}>
          <div style={{ fontWeight:700, fontSize:'15px', color:'white', flex:1 }}>{level.name}</div>
          <div style={{ fontSize:'11px', fontWeight:700, color:color, background:`${color}18`, padding:'3px 10px', borderRadius:'20px' }}>
            {pct >= 95 ? 'FULL' : pct >= 80 ? 'NEAR FULL' : 'AVAILABLE'}
          </div>
        </div>

        {/* Main occupancy display */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'10px', marginBottom:'14px', textAlign:'center' }}>
          <div style={{ background:'#111111', borderRadius:'4px', padding:'12px' }}>
            <div style={{ fontSize:'28px', fontWeight:700, color:color, lineHeight:1 }}>{occupied}</div>
            <div style={{ fontSize:'10px', color:'#555555', marginTop:'4px' }}>Occupied</div>
          </div>
          <div style={{ background:'#111111', borderRadius:'4px', padding:'12px' }}>
            <div style={{ fontSize:'28px', fontWeight:700, color:'#22c55e', lineHeight:1 }}>{available}</div>
            <div style={{ fontSize:'10px', color:'#555555', marginTop:'4px' }}>Available</div>
          </div>
          <div style={{ background:'#111111', borderRadius:'4px', padding:'12px' }}>
            <div style={{ fontSize:'28px', fontWeight:700, color:'#a0a0a0', lineHeight:1 }}>{level.capacity}</div>
            <div style={{ fontSize:'10px', color:'#555555', marginTop:'4px' }}>Capacity</div>
          </div>
        </div>

        {/* Occupancy bar */}
        <div style={{ marginBottom:'14px' }}>
          <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'4px' }}>
            <span style={{ fontSize:'10px', color:'#555555' }}>Occupancy</span>
            <span style={{ fontSize:'11px', color:color, fontWeight:700 }}>{pct}%</span>
          </div>
          <div style={{ height:'8px', background:'rgba(255,255,255,0.07)', borderRadius:'4px', overflow:'hidden' }}>
            <div style={{ height:'100%', width:`${pct}%`, background:color, borderRadius:'4px', transition:'width 0.4s' }} />
          </div>
        </div>

        {/* Camera info */}
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px', marginBottom:'14px' }}>
          <div style={{ background:'rgba(34,197,94,0.06)', border:'1px solid rgba(34,197,94,0.15)', borderRadius:'4px', padding:'8px 10px' }}>
            <div style={{ fontSize:'9px', color:'#22c55e', fontWeight:700, marginBottom:'2px' }}>ENTRY CAMERA</div>
            <div style={{ fontSize:'11px', color:'#e5e5e5' }}>{level.entryCameraLabel}</div>
            <div style={{ fontSize:'10px', color:'#555555', fontFamily:'monospace' }}>{level.entryCameraId}</div>
            <div style={{ fontSize:'11px', color:'#22c55e', fontWeight:700, marginTop:'4px' }}>↓ In: {level.countIn}</div>
          </div>
          <div style={{ background:'rgba(59,130,246,0.06)', border:'1px solid rgba(59,130,246,0.15)', borderRadius:'4px', padding:'8px 10px' }}>
            <div style={{ fontSize:'9px', color:'#3b82f6', fontWeight:700, marginBottom:'2px' }}>EXIT CAMERA</div>
            <div style={{ fontSize:'11px', color:'#e5e5e5' }}>{level.exitCameraLabel}</div>
            <div style={{ fontSize:'10px', color:'#555555', fontFamily:'monospace' }}>{level.exitCameraId}</div>
            <div style={{ fontSize:'11px', color:'#3b82f6', fontWeight:700, marginTop:'4px' }}>↑ Out: {level.countOut}</div>
          </div>
        </div>

        {/* Manual controls */}
        <div style={{ display:'flex', gap:'6px', marginBottom:'8px' }}>
          <button onClick={() => simulateDetect('entry')} disabled={simulating} style={{ flex:1, padding:'9px', borderRadius:'4px', border:'none', background:'rgba(34,197,94,0.15)', color:'#4ade80', fontWeight:700, fontSize:'12px', cursor:'pointer' }}>
            {simulating ? '…' : '↓ Entry +1'}
          </button>
          <button onClick={() => simulateDetect('exit')} disabled={simulating || occupied === 0} style={{ flex:1, padding:'9px', borderRadius:'4px', border:'none', background:'rgba(59,130,246,0.15)', color:'#93c5fd', fontWeight:700, fontSize:'12px', cursor: occupied === 0 ? 'default' : 'pointer', opacity: occupied === 0 ? 0.4 : 1 }}>
            {simulating ? '…' : '↑ Exit +1'}
          </button>
          <button onClick={() => onReset(level.id)} style={{ padding:'9px 12px', borderRadius:'4px', border:'1px solid #1a1a1a', background:'#111111', color:'#555555', fontSize:'11px', cursor:'pointer' }}>
            ↺
          </button>
        </div>
        <div style={{ fontSize:'10px', color:'#444444', textAlign:'center' }}>Reset since {level.lastReset} · Manual buttons simulate camera detection</div>
      </div>
    </div>
  );
}

// ─── Analytics / Reporting Data ──────────────────────────────────────────────

type ReportPeriod = 'day' | 'week' | 'month' | 'year';
type ReportLevel  = 'all' | 'L-B2' | 'L-B1' | 'L-G' | 'L-R';
type ReportType   = 'staff' | 'visitor' | 'accessible' | 'ev' | 'all';

interface DailyBucket {
  label: string;   // hour "06:00", day "Mon", date "1 Apr", or month "Jan"
  entries: number;
  exits: number;
  peak: number;    // max concurrent occupancy during that bucket
}

// Generate realistic daily hourly data
function makeDailyBuckets(): DailyBucket[] {
  const hours = ['06','07','08','09','10','11','12','13','14','15','16','17','18','19','20','21','22'];
  const profile = [8,22,48,71,60,52,64,58,45,54,61,72,48,32,22,14,6];
  return hours.map((h, i) => ({
    label: `${h}:00`,
    entries: profile[i],
    exits: Math.max(0, profile[i] - 8 + Math.floor(Math.random() * 6)),
    peak: Math.round(profile[i] * 2.1),
  }));
}
function makeWeeklyBuckets(): DailyBucket[] {
  const days = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
  const profile = [312,341,328,355,298,142,68];
  return days.map((d, i) => ({
    label: d, entries: profile[i],
    exits: Math.round(profile[i] * 0.97),
    peak: Math.round(profile[i] * 0.58),
  }));
}
function makeMonthlyBuckets(): DailyBucket[] {
  return Array.from({ length: 30 }, (_, i) => {
    const base = 280 + Math.round(Math.sin(i / 3) * 40) + Math.round(Math.random() * 30);
    return { label: String(i + 1), entries: base, exits: Math.round(base * 0.96), peak: Math.round(base * 0.55) };
  });
}
function makeYearlyBuckets(): DailyBucket[] {
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const profile = [7200,6800,8100,8600,9200,8400,7100,7800,9100,9400,8800,7500];
  return months.map((m, i) => ({
    label: m, entries: profile[i],
    exits: Math.round(profile[i] * 0.97),
    peak: Math.round(profile[i] * 0.055),
  }));
}

const ANALYTICS_DATA: Record<ReportPeriod, DailyBucket[]> = {
  day:   makeDailyBuckets(),
  week:  makeWeeklyBuckets(),
  month: makeMonthlyBuckets(),
  year:  makeYearlyBuckets(),
};

const PERIOD_LABELS: Record<ReportPeriod, string> = { day:'Today', week:'This Week', month:'This Month', year:'This Year' };

// Revenue data ($4/hr visitor bays)
const REVENUE_DATA: Record<ReportPeriod, { gross: string; visitorExits: number }> = {
  day:   { gross: '$621',     visitorExits: 78   },
  week:  { gross: '$5,842',   visitorExits: 712  },
  month: { gross: '$26,180',  visitorExits: 3140 },
  year:  { gross: '$314,880', visitorExits: 38560},
};

// Heatmap — occupancy % per day × hour (Mon–Sun, 6am–10pm)
const HEATMAP_HOURS = ['6am','7am','8am','9am','10am','11am','12pm','1pm','2pm','3pm','4pm','5pm','6pm','7pm','8pm','9pm','10pm'];
const HEATMAP_DAYS  = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
const HEATMAP_DATA: number[][] = [
  [5,18,62,88,74,68,78,72,58,66,74,85,62,42,28,16,6],
  [4,20,68,91,78,70,82,75,62,68,76,88,64,44,26,14,5],
  [6,22,65,87,76,72,80,74,60,70,78,84,60,40,24,12,4],
  [5,24,72,94,82,74,86,79,66,74,82,90,68,46,30,18,7],
  [4,18,58,80,68,66,74,66,52,58,66,76,54,36,20,10,4],
  [2,6,14,28,38,46,52,56,48,44,36,28,22,14,8,4,2],
  [1,3,7,12,18,24,28,30,26,22,18,14,10,6,3,2,1],
];

// Dwell time distribution
interface DwellBucket { label: string; pct: number; color: string; }
const DWELL_DATA: Record<ReportPeriod, DwellBucket[]> = {
  day: [
    { label:'< 30 min', pct:12, color:'#22c55e' },
    { label:'30–60 min', pct:18, color:'#84cc16' },
    { label:'1–2 hrs',  pct:28, color:'#f59e0b' },
    { label:'2–4 hrs',  pct:25, color:'#f97316' },
    { label:'4–8 hrs',  pct:14, color:'#ef4444' },
    { label:'8 hrs+',   pct:3,  color:'#991b1b' },
  ],
  week: [
    { label:'< 30 min', pct:10, color:'#22c55e' },
    { label:'30–60 min', pct:16, color:'#84cc16' },
    { label:'1–2 hrs',  pct:30, color:'#f59e0b' },
    { label:'2–4 hrs',  pct:27, color:'#f97316' },
    { label:'4–8 hrs',  pct:13, color:'#ef4444' },
    { label:'8 hrs+',   pct:4,  color:'#991b1b' },
  ],
  month: [
    { label:'< 30 min', pct:11, color:'#22c55e' },
    { label:'30–60 min', pct:17, color:'#84cc16' },
    { label:'1–2 hrs',  pct:29, color:'#f59e0b' },
    { label:'2–4 hrs',  pct:26, color:'#f97316' },
    { label:'4–8 hrs',  pct:13, color:'#ef4444' },
    { label:'8 hrs+',   pct:4,  color:'#991b1b' },
  ],
  year: [
    { label:'< 30 min', pct:9,  color:'#22c55e' },
    { label:'30–60 min', pct:15, color:'#84cc16' },
    { label:'1–2 hrs',  pct:31, color:'#f59e0b' },
    { label:'2–4 hrs',  pct:28, color:'#f97316' },
    { label:'4–8 hrs',  pct:13, color:'#ef4444' },
    { label:'8 hrs+',   pct:4,  color:'#991b1b' },
  ],
};

// Overstay alerts
interface OverstayAlert {
  id: string;
  plate: string;
  zone: string;
  level: string;
  entryTime: string;
  duration: string;
  limitHrs: number;
  overstayBy: string;
  severity: 'warning' | 'critical';
  status: 'active' | 'noted' | 'cleared';
  note?: string;
}
const SEED_OVERSTAY: OverstayAlert[] = [
  { id:'OS-001', plate:'NSW 1XH-422',  zone:'P2 Visitor',   level:'Ground', entryTime:'06:18', duration:'8h 22m', limitHrs:4, overstayBy:'4h 22m', severity:'critical', status:'active' },
  { id:'OS-002', plate:'NSW 8YP-432',  zone:'P2 Visitor',   level:'Ground', entryTime:'07:55', duration:'6h 44m', limitHrs:4, overstayBy:'2h 44m', severity:'critical', status:'active' },
  { id:'OS-003', plate:'NSW 3LT-771',  zone:'P4 EV',        level:'B1',     entryTime:'08:02', duration:'4h 12m', limitHrs:4, overstayBy:'12m',    severity:'warning',  status:'noted',  note:'Driver notified via intercom' },
  { id:'OS-004', plate:'NSW 7KR-290',  zone:'P2 Visitor',   level:'Ground', entryTime:'05:44', duration:'9h 36m', limitHrs:4, overstayBy:'5h 36m', severity:'critical', status:'active' },
  { id:'OS-005', plate:'NSW 2BD-994',  zone:'P4 EV',        level:'B1',     entryTime:'08:21', duration:'2h 18m', limitHrs:2, overstayBy:'18m',    severity:'warning',  status:'active' },
  { id:'OS-006', plate:'NSW KX11AA',   zone:'P3 Accessible',level:'Ground', entryTime:'07:10', duration:'3h 22m', limitHrs:4, overstayBy:'—',      severity:'warning',  status:'cleared' },
];

const PERIOD_SUMMARY: Record<ReportPeriod, { entries:number; exits:number; peak:number; avgDuration:string; uniqueVehicles:number }> = {
  day:   { entries:208,   exits:174,   peak:87,  avgDuration:'2h 14m', uniqueVehicles:196  },
  week:  { entries:1844,  exits:1798,  peak:98,  avgDuration:'2h 31m', uniqueVehicles:1122 },
  month: { entries:7820,  exits:7641,  peak:112, avgDuration:'2h 22m', uniqueVehicles:3840 },
  year:  { entries:98400, exits:96100, peak:134, avgDuration:'2h 18m', uniqueVehicles:28400},
};

// Mini bar chart component
function MiniBarChart({ data, color = '#3b82f6', maxOverride }: { data: DailyBucket[]; color?: string; maxOverride?: number }) {
  const maxVal = maxOverride || Math.max(...data.map(d => d.entries), 1);
  const show = data.length > 20 ? data.filter((_, i) => i % 3 === 0) : data;
  return (
    <div style={{ display:'flex', alignItems:'flex-end', gap:'2px', height:'80px' }}>
      {show.map((d, i) => (
        <div key={i} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:'2px' }}>
          <div style={{ width:'100%', background:`${color}cc`, borderRadius:'2px 2px 0 0', height:`${Math.max(2, Math.round((d.entries / maxVal) * 72))}px`, minHeight:'2px' }} />
          {show.length <= 12 && <div style={{ fontSize:'8px', color:'#444444', transform:'rotate(-30deg)', whiteSpace:'nowrap', transformOrigin:'top center', marginTop:'4px' }}>{d.label}</div>}
        </div>
      ))}
    </div>
  );
}

// Email preview modal
function EmailReportModal({
  period, levelFilter, typeFilter, onClose,
}: {
  period: ReportPeriod; levelFilter: ReportLevel; typeFilter: ReportType; onClose: () => void;
}) {
  const { toast } = useToast();
  const [recipient, setRecipient] = useState('manager@parramatta.nsw.gov.au');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const summary = PERIOD_SUMMARY[period];
  const today = new Date().toLocaleDateString('en-AU', { weekday:'long', day:'numeric', month:'long', year:'numeric' });

  function handleSend() {
    const subject = encodeURIComponent('Car Park Report — Lemai');
    const body = encodeURIComponent(`Please find the Car Park report attached.\nPeriod: ${PERIOD_LABELS[period]}`);
    window.location.href = `mailto:${encodeURIComponent(recipient)}?subject=${subject}&body=${body}`;
    onClose();
  }

  const levelLabel = levelFilter === 'all' ? 'All Levels' : levelFilter;
  const typeLabel  = typeFilter  === 'all' ? 'All Vehicle Types' : typeFilter.charAt(0).toUpperCase() + typeFilter.slice(1);

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center', padding:'20px' }}>
      <div style={{ background:'#0a0a0a', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'18px', width:'100%', maxWidth:'620px', maxHeight:'90vh', overflowY:'auto' }}>
        <div style={{ padding:'18px 22px 14px', borderBottom:'1px solid rgba(255,255,255,0.07)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div style={{ fontWeight:700, fontSize:'15px', color:'white' }}>Email Car Park Report</div>
          <button onClick={onClose} style={{ background:'rgba(255,255,255,0.06)', border:'none', borderRadius:'4px', color:'#555555', width:'30px', height:'30px', fontSize:'15px', cursor:'pointer' }}>✕</button>
        </div>

        <div style={{ padding:'18px 22px' }}>
          {/* Recipient */}
          <div style={{ marginBottom:'14px' }}>
            <label style={{ fontSize:'11px', color:'#555555', display:'block', marginBottom:'5px', fontWeight:600 }}>SEND TO</label>
            <input value={recipient} onChange={e => setRecipient(e.target.value)}
              style={{ width:'100%', boxSizing:'border-box', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'4px', padding:'9px 12px', color:'white', fontSize:'13px' }} />
          </div>
          {/* Filters summary */}
          <div style={{ display:'flex', gap:'8px', marginBottom:'16px', flexWrap:'wrap' }}>
            {[
              { label:'Period',     value:PERIOD_LABELS[period] },
              { label:'Level',      value:levelLabel },
              { label:'Type',       value:typeLabel },
            ].map(f => (
              <div key={f.label} style={{ background:'rgba(59,130,246,0.1)', border:'1px solid rgba(59,130,246,0.2)', borderRadius:'4px', padding:'6px 12px', fontSize:'12px' }}>
                <span style={{ color:'#555555' }}>{f.label}: </span><span style={{ color:'#93c5fd', fontWeight:600 }}>{f.value}</span>
              </div>
            ))}
          </div>

          {/* Email preview */}
          <div style={{ background:'white', borderRadius:'5px', overflow:'hidden', marginBottom:'16px' }}>
            {/* Header */}
            <div style={{ background:'linear-gradient(135deg,#1e3a5f,#1d4ed8)', padding:'20px 24px' }}>
              <div style={{ fontWeight:800, fontSize:'18px', color:'white' }}>lemai</div>
              <div style={{ fontSize:'12px', color:'rgba(255,255,255,0.7)', marginTop:'2px' }}>City of Parramatta Council — Car Park Report</div>
            </div>
            <div style={{ padding:'20px 24px' }}>
              <div style={{ fontSize:'13px', color:'#374151', marginBottom:'4px' }}>Report Period: <strong>{PERIOD_LABELS[period]}</strong></div>
              <div style={{ fontSize:'12px', color:'#6b7280', marginBottom:'16px' }}>Generated: {today} · Filter: {levelLabel}, {typeLabel}</div>

              {/* KPI grid */}
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'10px', marginBottom:'18px' }}>
                {[
                  { label:'Total Entries',   value:summary.entries.toLocaleString(), color:'#1d4ed8' },
                  { label:'Total Exits',     value:summary.exits.toLocaleString(),   color:'#059669' },
                  { label:'Peak Occupancy',  value:String(summary.peak),             color:'#dc2626' },
                  { label:'Unique Vehicles', value:summary.uniqueVehicles.toLocaleString(), color:'#7c3aed' },
                  { label:'Avg Stay',        value:summary.avgDuration,              color:'#d97706' },
                  { label:'Utilisation',     value:`${Math.round((summary.peak / 440) * 100)}%`, color:'#0891b2' },
                ].map(k => (
                  <div key={k.label} style={{ background:'#f8fafc', borderRadius:'4px', padding:'12px', textAlign:'center', border:'1px solid #e2e8f0' }}>
                    <div style={{ fontSize:'20px', fontWeight:800, color:k.color }}>{k.value}</div>
                    <div style={{ fontSize:'10px', color:'#6b7280', marginTop:'3px' }}>{k.label}</div>
                  </div>
                ))}
              </div>

              {/* Level breakdown table */}
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'12px', marginBottom:'14px' }}>
                <thead>
                  <tr style={{ background:'#f1f5f9' }}>
                    {['Level','Entries','Exits','Peak Occ.','Utilisation'].map(h => (
                      <th key={h} style={{ padding:'7px 10px', textAlign:'left', color:'#374151', fontWeight:700, fontSize:'11px' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    { name:'Level B2',  entries:Math.round(summary.entries*0.38), exits:Math.round(summary.exits*0.37), peak:46, cap:120 },
                    { name:'Level B1',  entries:Math.round(summary.entries*0.31), exits:Math.round(summary.exits*0.31), peak:38, cap:80  },
                    { name:'Ground',    entries:Math.round(summary.entries*0.16), exits:Math.round(summary.exits*0.17), peak:22, cap:40  },
                    { name:'Roof (P6)', entries:Math.round(summary.entries*0.15), exits:Math.round(summary.exits*0.15), peak:8,  cap:200 },
                  ].map((r, i) => (
                    <tr key={r.name} style={{ background:i%2===0?'white':'#f8fafc', borderBottom:'1px solid #f1f5f9' }}>
                      <td style={{ padding:'7px 10px', fontWeight:600, color:'#374151' }}>{r.name}</td>
                      <td style={{ padding:'7px 10px', color:'#374151' }}>{r.entries.toLocaleString()}</td>
                      <td style={{ padding:'7px 10px', color:'#374151' }}>{r.exits.toLocaleString()}</td>
                      <td style={{ padding:'7px 10px', color:'#dc2626', fontWeight:600 }}>{r.peak}</td>
                      <td style={{ padding:'7px 10px', color:'#374151' }}>{Math.round((r.peak/r.cap)*100)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ fontSize:'10px', color:'#9ca3af', borderTop:'1px solid #f1f5f9', paddingTop:'10px' }}>
                This report was generated automatically by Lemai — City of Parramatta Council Security Intelligence Platform
              </div>
            </div>
          </div>

          <div style={{ display:'flex', gap:'10px', justifyContent:'flex-end' }}>
              <button onClick={onClose} style={{ padding:'10px 22px', borderRadius:'4px', border:'1px solid rgba(255,255,255,0.1)', background:'transparent', color:'#555555', fontSize:'13px', fontWeight:600, cursor:'pointer' }}>Cancel</button>
              <button onClick={handleSend} disabled={!recipient.trim()} style={{ padding:'10px 28px', borderRadius:'4px', border:'none', background:'linear-gradient(135deg,#3b82f6,#1d4ed8)', color:'white', fontSize:'13px', fontWeight:700, cursor:'pointer' }}>
                Open Email Client
              </button>
            </div>
        </div>
      </div>
    </div>
  );
}

// ─── Scheduled Reports Panel ──────────────────────────────────────────────────

function ScheduledReportPanel() {
  const [dailyOn, setDailyOn]   = useState(true);
  const [weeklyOn, setWeeklyOn] = useState(true);
  const [sendTime, setSendTime] = useState('07:00');
  const [weekDay, setWeekDay]   = useState('Mon');
  const [recipients, setRecipients] = useState('manager@parramatta.nsw.gov.au');
  const [saved, setSaved] = useState(false);

  function save() { setSaved(true); setTimeout(() => setSaved(false), 2500); }

  function Toggle({ on, onToggle }: { on: boolean; onToggle: () => void }) {
    return (
      <button onClick={onToggle} style={{ width:'38px', height:'20px', borderRadius:'4px', border:'none', cursor:'pointer', background: on ? '#3b82f6' : 'rgba(255,255,255,0.1)', position:'relative', transition:'background 0.2s', flexShrink:0 }}>
        <div style={{ position:'absolute', top:'2px', left: on ? '20px' : '2px', width:'16px', height:'16px', borderRadius:'50%', background:'white', transition:'left 0.2s' }} />
      </button>
    );
  }

  return (
    <div style={{ background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:'5px', padding:'20px' }}>
      <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'16px' }}>
        <div style={{ fontWeight:700, fontSize:'14px', color:'white', flex:1 }}>⏰ Scheduled Reports</div>
        <div style={{ fontSize:'11px', color:'#22c55e', background:'rgba(34,197,94,0.08)', border:'1px solid rgba(34,197,94,0.2)', borderRadius:'6px', padding:'3px 10px' }}>Auto-sends at set time</div>
      </div>

      <div style={{ marginBottom:'16px' }}>
        <label style={{ fontSize:'10px', color:'#555555', display:'block', marginBottom:'5px', fontWeight:700, letterSpacing:'0.05em' }}>RECIPIENTS</label>
        <input value={recipients} onChange={e => setRecipients(e.target.value)}
          placeholder="email1@example.com, email2@example.com"
          style={{ width:'100%', boxSizing:'border-box', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'4px', padding:'9px 12px', color:'white', fontSize:'13px' }} />
        <div style={{ fontSize:'10px', color:'#555555', marginTop:'4px' }}>Separate multiple emails with commas</div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px', marginBottom:'16px' }}>
        {/* Daily */}
        <div style={{ background:'#0d0d0d', border:`1px solid ${dailyOn?'rgba(59,130,246,0.3)':'rgba(255,255,255,0.07)'}`, borderRadius:'5px', padding:'16px' }}>
          <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'12px' }}>
            <span style={{ flex:1, fontWeight:700, fontSize:'13px', color: dailyOn ? 'white' : '#555555' }}>📅 Daily Report</span>
            <Toggle on={dailyOn} onToggle={() => setDailyOn(v => !v)} />
          </div>
          <div style={{ opacity: dailyOn ? 1 : 0.35, transition:'opacity 0.2s' }}>
            <label style={{ fontSize:'10px', color:'#555555', display:'block', marginBottom:'4px' }}>SEND TIME</label>
            <input type="time" value={sendTime} onChange={e => setSendTime(e.target.value)} disabled={!dailyOn}
              style={{ background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'6px', padding:'7px 10px', color:'white', fontSize:'13px', width:'100%', boxSizing:'border-box' }} />
            {dailyOn && <div style={{ fontSize:'10px', color:'#22c55e', marginTop:'8px' }}>✓ Next: Tomorrow {sendTime} AEST</div>}
            <div style={{ fontSize:'10px', color:'#555555', marginTop:'4px' }}>Entries, exits, revenue, occupancy</div>
          </div>
        </div>

        {/* Weekly */}
        <div style={{ background:'#0d0d0d', border:`1px solid ${weeklyOn?'rgba(59,130,246,0.3)':'rgba(255,255,255,0.07)'}`, borderRadius:'5px', padding:'16px' }}>
          <div style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'12px' }}>
            <span style={{ flex:1, fontWeight:700, fontSize:'13px', color: weeklyOn ? 'white' : '#555555' }}>📊 Weekly Summary</span>
            <Toggle on={weeklyOn} onToggle={() => setWeeklyOn(v => !v)} />
          </div>
          <div style={{ opacity: weeklyOn ? 1 : 0.35, transition:'opacity 0.2s' }}>
            <label style={{ fontSize:'10px', color:'#555555', display:'block', marginBottom:'4px' }}>SEND DAY</label>
            <select value={weekDay} onChange={e => setWeekDay(e.target.value)} disabled={!weeklyOn}
              style={{ background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'6px', padding:'7px 10px', color:'white', fontSize:'13px', width:'100%' }}>
              {['Mon','Tue','Wed','Thu','Fri'].map(d => <option key={d} value={d}>{d} at {sendTime}</option>)}
            </select>
            {weeklyOn && <div style={{ fontSize:'10px', color:'#22c55e', marginTop:'8px' }}>✓ Next: {weekDay} at {sendTime} AEST</div>}
            <div style={{ fontSize:'10px', color:'#555555', marginTop:'4px' }}>Heatmap, revenue, dwell time analysis</div>
          </div>
        </div>
      </div>

      <div style={{ display:'flex', justifyContent:'flex-end' }}>
        <button onClick={save} style={{ padding:'9px 24px', background: saved ? 'rgba(34,197,94,0.15)' : 'linear-gradient(135deg,#3b82f6,#1d4ed8)', border: saved ? '1px solid rgba(34,197,94,0.3)' : 'none', borderRadius:'4px', color: saved ? '#22c55e' : 'white', fontWeight:700, fontSize:'13px', cursor:'pointer', transition:'all 0.2s' }}>
          {saved ? '✓ Schedule Saved' : 'Save Schedule'}
        </button>
      </div>
    </div>
  );
}

// ─── Occupancy Heatmap ────────────────────────────────────────────────────────

function OccupancyHeatmap() {
  function heatColor(pct: number): string {
    if (pct < 20) return 'rgba(34,197,94,0.15)';
    if (pct < 40) return 'rgba(34,197,94,0.4)';
    if (pct < 60) return 'rgba(245,158,11,0.45)';
    if (pct < 80) return 'rgba(249,115,22,0.6)';
    if (pct < 95) return 'rgba(239,68,68,0.75)';
    return 'rgba(239,68,68,0.95)';
  }
  const LEGEND = [
    { bg:'rgba(34,197,94,0.15)',  label:'<20%'  },
    { bg:'rgba(34,197,94,0.4)',   label:'20–40%' },
    { bg:'rgba(245,158,11,0.45)', label:'40–60%' },
    { bg:'rgba(249,115,22,0.6)',  label:'60–80%' },
    { bg:'rgba(239,68,68,0.75)', label:'80–95%' },
    { bg:'rgba(239,68,68,0.95)', label:'95%+'   },
  ];
  return (
    <div style={{ background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:'5px', padding:'20px', overflowX:'auto' }}>
      <div style={{ fontWeight:700, fontSize:'13px', color:'white', marginBottom:'2px' }}>Occupancy Heatmap</div>
      <div style={{ fontSize:'11px', color:'#555555', marginBottom:'14px' }}>Average % occupancy by day and hour — spot your peaks instantly</div>
      <div style={{ display:'grid', gridTemplateColumns:`48px repeat(${HEATMAP_HOURS.length},1fr)`, gap:'3px', minWidth:'560px' }}>
        <div />
        {HEATMAP_HOURS.map(h => (
          <div key={h} style={{ fontSize:'8px', color:'#555555', textAlign:'center', paddingBottom:'4px', fontWeight:600 }}>{h}</div>
        ))}
        {HEATMAP_DAYS.map((day, di) => (
          <React.Fragment key={day}>
            <div style={{ fontSize:'11px', color:'#555555', display:'flex', alignItems:'center', fontWeight:600 }}>{day}</div>
            {HEATMAP_DATA[di].map((pct, hi) => (
              <div key={hi} title={`${day} ${HEATMAP_HOURS[hi]}: ${pct}% occupancy`}
                style={{ height:'26px', borderRadius:'3px', background:heatColor(pct), display:'flex', alignItems:'center', justifyContent:'center' }}>
                {pct >= 65 && <span style={{ fontSize:'7px', color:'rgba(255,255,255,0.85)', fontWeight:700 }}>{pct}</span>}
              </div>
            ))}
          </React.Fragment>
        ))}
      </div>
      <div style={{ display:'flex', gap:'10px', marginTop:'12px', alignItems:'center', flexWrap:'wrap' }}>
        <span style={{ fontSize:'10px', color:'#555555' }}>Low →</span>
        {LEGEND.map(l => (
          <div key={l.label} style={{ display:'flex', alignItems:'center', gap:'4px' }}>
            <div style={{ width:'14px', height:'10px', borderRadius:'2px', background:l.bg }} />
            <span style={{ fontSize:'9px', color:'#555555' }}>{l.label}</span>
          </div>
        ))}
        <span style={{ fontSize:'10px', color:'#555555' }}>→ High</span>
      </div>
    </div>
  );
}

// ─── Dwell Time Chart ─────────────────────────────────────────────────────────

function DwellChart({ data }: { data: DwellBucket[] }) {
  return (
    <div style={{ background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:'5px', padding:'20px' }}>
      <div style={{ fontWeight:700, fontSize:'13px', color:'white', marginBottom:'2px' }}>Dwell Time Distribution</div>
      <div style={{ fontSize:'11px', color:'#555555', marginBottom:'14px' }}>How long visitors stay — informs pricing and bay turnover decisions</div>
      <div style={{ display:'flex', height:'28px', borderRadius:'4px', overflow:'hidden', marginBottom:'16px', gap:'1px' }}>
        {data.map(b => (
          <div key={b.label} title={`${b.label}: ${b.pct}%`}
            style={{ flex:b.pct, background:b.color, display:'flex', alignItems:'center', justifyContent:'center' }}>
            {b.pct >= 12 && <span style={{ fontSize:'10px', color:'white', fontWeight:700 }}>{b.pct}%</span>}
          </div>
        ))}
      </div>
      <div style={{ display:'flex', flexDirection:'column', gap:'7px' }}>
        {data.map(b => (
          <div key={b.label} style={{ display:'flex', alignItems:'center', gap:'10px' }}>
            <div style={{ width:'10px', height:'10px', borderRadius:'2px', background:b.color, flexShrink:0 }} />
            <div style={{ fontSize:'11px', color:'#a0a0a0', width:'84px', flexShrink:0 }}>{b.label}</div>
            <div style={{ flex:1, height:'5px', background:'rgba(255,255,255,0.06)', borderRadius:'3px' }}>
              <div style={{ height:'100%', width:`${b.pct}%`, background:b.color, borderRadius:'3px' }} />
            </div>
            <div style={{ fontSize:'12px', color:'white', fontWeight:700, width:'32px', textAlign:'right' }}>{b.pct}%</div>
          </div>
        ))}
      </div>
      <div style={{ marginTop:'14px', padding:'10px 12px', background:'rgba(59,130,246,0.06)', border:'1px solid rgba(59,130,246,0.15)', borderRadius:'4px', fontSize:'11px', color:'#555555' }}>
        💡 <strong style={{ color:'#93c5fd' }}>Insight:</strong> {data[2].pct + data[3].pct}% of visitors stay 1–4 hours. Consider tiered pricing to incentivise turnover during peak periods.
      </div>
    </div>
  );
}

interface Zone {
  id: string;
  name: string;
  level: string;
  total: number;
  occupied: number;
  reserved: number;
  type: 'staff'|'visitor'|'accessible'|'ev';
  status: 'normal'|'near-full'|'full';
  rate?: string;
}

interface EntryEvent {
  id: number;
  plate: string;
  type: string;
  zone: string;
  action: 'entry'|'exit';
  time: string;
  duration?: string;
}

const ZONES: Zone[] = [
  { id:'p1', name:'P1 Staff Carpark',      level:'Ground',  total:120, occupied:87,  reserved:10, type:'staff',      status:'normal',    rate:'Free' },
  { id:'p2', name:'P2 Visitor Carpark',    level:'Ground',  total:80,  occupied:71,  reserved:5,  type:'visitor',    status:'near-full', rate:'$4/hr' },
  { id:'p3', name:'P3 Accessible',         level:'Ground',  total:15,  occupied:4,   reserved:0,  type:'accessible', status:'normal',    rate:'Free' },
  { id:'p4', name:'P4 EV Charging',        level:'B1',      total:12,  occupied:8,   reserved:0,  type:'ev',         status:'normal',    rate:'$2/hr + charge' },
  { id:'p5', name:'P5 Council Fleet',      level:'B1',      total:40,  occupied:22,  reserved:40, type:'staff',      status:'normal',    rate:'Reserved' },
  { id:'p6', name:'P6 Event Overflow',     level:'Roof',    total:200, occupied:12,  reserved:0,  type:'visitor',    status:'normal',    rate:'$6/hr events' },
];

const EVENTS: EntryEvent[] = [
  { id:1,  plate:'NSW BK92ZX', type:'Staff',    zone:'P1', action:'entry', time:'08:41:22' },
  { id:2,  plate:'NSW 1XH-422',type:'Visitor',  zone:'P2', action:'entry', time:'08:39:05' },
  { id:3,  plate:'NSW 7AC-119', type:'Staff',   zone:'P1', action:'exit',  time:'08:35:11', duration:'7h 23m' },
  { id:4,  plate:'NSW 2BD-994', type:'EV',      zone:'P4', action:'entry', time:'08:21:44' },
  { id:5,  plate:'NSW 9XZ-001', type:'Visitor', zone:'P2', action:'exit',  time:'08:18:33', duration:'2h 14m' },
  { id:6,  plate:'NSW KJ44MN',  type:'Staff',   zone:'P5', action:'entry', time:'08:10:02' },
  { id:7,  plate:'NSW 3LT-771', type:'Accessible',zone:'P3',action:'entry',time:'08:02:54' },
  { id:8,  plate:'NSW 8YP-432', type:'Visitor', zone:'P2', action:'entry', time:'07:55:18' },
];

const ZONE_COLOR: Record<string,string> = { staff:'#3b82f6', visitor:'#a855f7', accessible:'#22c55e', ev:'#06b6d4' };
const ZONE_ICON:  Record<string,string> = { staff:'👤', visitor:'🅿️', accessible:'♿', ev:'⚡' };

// ─── Bookings Seed Data ────────────────────────────────────────────────────────

interface ParkingBooking {
  id: string;
  ref: string;
  name: string;
  plate: string;
  bay: string;
  level: string;
  date: string;
  timeIn: string;
  timeOut: string;
  status: 'confirmed' | 'active' | 'completed' | 'cancelled';
  type: 'staff' | 'visitor' | 'ev' | 'accessible';
}

const SEED_BOOKINGS: ParkingBooking[] = [
  { id:'BK-001', ref:'BK-2024-001', name:'James Okafor',      plate:'NSW 3LT-771', bay:'A12', level:'Level B1', date:'Today',      timeIn:'07:00', timeOut:'18:00', status:'active',    type:'staff'      },
  { id:'BK-002', ref:'BK-2024-002', name:'Visitor — Suite 5', plate:'NSW 1XH-422', bay:'V03', level:'Ground',   date:'Today',      timeIn:'09:00', timeOut:'12:00', status:'active',    type:'visitor'    },
  { id:'BK-003', ref:'BK-2024-003', name:'Sarah Chen',        plate:'NSW 4MT-220', bay:'E02', level:'Level B2', date:'Today',      timeIn:'08:00', timeOut:'17:00', status:'active',    type:'ev'         },
  { id:'BK-004', ref:'BK-2024-004', name:'Michael Torres',    plate:'NSW 7AC-119', bay:'A07', level:'Level B1', date:'Today',      timeIn:'06:30', timeOut:'15:30', status:'completed', type:'staff'      },
  { id:'BK-005', ref:'BK-2024-005', name:'Delivery — Dock 1', plate:'NSW 2BD-994', bay:'D01', level:'Ground',   date:'Today',      timeIn:'11:00', timeOut:'11:45', status:'confirmed', type:'visitor'    },
  { id:'BK-006', ref:'BK-2024-006', name:'Rachel Kim',        plate:'NSW KJ44MN',  bay:'HC1', level:'Ground',   date:'Today',      timeIn:'08:30', timeOut:'16:30', status:'active',    type:'accessible' },
  { id:'BK-007', ref:'BK-2024-007', name:'David Walsh',       plate:'NSW 8YP-432', bay:'V11', level:'Level B2', date:'Tomorrow',   timeIn:'09:00', timeOut:'17:00', status:'confirmed', type:'visitor'    },
  { id:'BK-008', ref:'BK-2024-008', name:'Anna Petrov',       plate:'NSW 5NQ-887', bay:'A22', level:'Roof P6',  date:'Tomorrow',   timeIn:'07:30', timeOut:'18:30', status:'confirmed', type:'staff'      },
  { id:'BK-009', ref:'BK-2024-009', name:'Chris Nguyen',      plate:'NSW 6RK-300', bay:'E04', level:'Level B2', date:'Tomorrow',   timeIn:'08:00', timeOut:'19:00', status:'confirmed', type:'ev'         },
  { id:'BK-010', ref:'BK-2024-010', name:'Tech Summit — VIP', plate:'Multiple',    bay:'V01-V05', level:'Ground', date:'14 Apr', timeIn:'08:00', timeOut:'20:00', status:'confirmed', type:'visitor'    },
];

const BOOKING_STATUS_COLOR: Record<string,string> = { confirmed:'#3b82f6', active:'#22c55e', completed:'#555555', cancelled:'#ef4444' };
const BOOKING_TYPE_COLOR:   Record<string,string> = { staff:'#3b82f6', visitor:'#a855f7', accessible:'#22c55e', ev:'#06b6d4' };

export default function CarPark() {
  const { isDemoOrg } = useLicense();
  const [tab, setTab] = useState<'overview'|'counting'|'analytics'|'vehicle-ai'|'events'|'zones'|'alerts'|'bookings'>('counting');
  const { editing, setEditing, slots, isVisible: isCPVisible, orderedWidgets: cpWidgets, toggle: cpToggle, moveUp: cpMoveUp, moveDown: cpMoveDown, reset: cpReset } = usePageCustomizer('carpark', CARPARK_WIDGETS);
  const vehicleDetections = isDemoOrg ? generateVehicleDetections(25) : [];
  const [overstay, setOverstay] = useState<OverstayAlert[]>(() => isDemoOrg ? SEED_OVERSTAY : []);
  const [overstayLimit, setOverstayLimit] = useState(4);
  const [tick, setTick] = useState(0);
  const [levels, setLevels] = useState<ParkingLevel[]>(() => isDemoOrg ? SEED_LEVELS : []);
  const [countEvents, setCountEvents] = useState<CountEvent[]>(() => isDemoOrg ? SEED_COUNT_EVENTS : []);
  const [initialLoading, setInitialLoading] = useState(true);
  const [bannerDismissed, setBannerDismissed] = React.useState(
    sessionStorage.getItem('carpark_banner_dismissed') === 'true'
  );

  useEffect(() => {
    const t = setInterval(() => setTick(x => x+1), 20000);
    return () => clearInterval(t);
  }, []);

  // Re-sync seed data when org switches
  useEffect(() => {
    setLevels(isDemoOrg ? SEED_LEVELS : []);
    setCountEvents(isDemoOrg ? SEED_COUNT_EVENTS : []);
    setOverstay(isDemoOrg ? SEED_OVERSTAY : []);
  }, [isDemoOrg]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const t = setTimeout(() => setInitialLoading(false), 650);
    return () => clearTimeout(t);
  }, []);

  const handleEntry = useCallback((levelId: string) => {
    setLevels(prev => prev.map(l => l.id === levelId ? { ...l, countIn: l.countIn + 1 } : l));
    const level = levels.find(l => l.id === levelId);
    setCountEvents(prev => [{
      id: nextEvtId(), levelId, levelName: level?.name || levelId,
      direction: 'entry' as const, time: nowTime(), source: 'manual' as const,
    }, ...prev].slice(0, 50));
  }, [levels]);

  const handleExit = useCallback((levelId: string) => {
    setLevels(prev => prev.map(l => l.id === levelId ? { ...l, countOut: l.countOut + 1 } : l));
    const level = levels.find(l => l.id === levelId);
    setCountEvents(prev => [{
      id: nextEvtId(), levelId, levelName: level?.name || levelId,
      direction: 'exit' as const, time: nowTime(), source: 'manual' as const,
    }, ...prev].slice(0, 50));
  }, [levels]);

  const handleReset = useCallback((levelId: string) => {
    setLevels(prev => prev.map(l => l.id === levelId ? { ...l, countIn: 0, countOut: 0, lastReset: nowTime().slice(0,5) } : l));
  }, []);

  // Analytics state
  const [period, setPeriod]           = useState<ReportPeriod>('day');
  const [levelFilter, setLevelFilter] = useState<ReportLevel>('all');
  const [typeFilter, setTypeFilter]   = useState<ReportType>('all');
  const [showEmail, setShowEmail]     = useState(false);

  const totalSpaces  = ZONES.reduce((a,z) => a+z.total, 0);
  const totalOcc     = ZONES.reduce((a,z) => a+z.occupied, 0);
  const available    = totalSpaces - totalOcc;
  const overallPct   = Math.round((totalOcc/totalSpaces)*100);

  const tabStyle = (t: string): React.CSSProperties => ({
    padding:'6px 16px', border:'none', background:'none', cursor:'pointer', fontSize:'12px', fontWeight:600,
    color: tab===t?'#e2e8f0':'#555555',
    borderBottom: tab===t?'2px solid #3b82f6':'2px solid transparent',
    transition:'all 0.15s',
  });

  return (
    <Layout title="Car Park" subtitle="Multi-site parking management and LPR monitoring">
      {/* Demo mode banner */}
      {!bannerDismissed && (
        <div style={{ backgroundColor:'#172554', border:'1px solid #3b82f6', padding:'8px 16px', marginBottom:'14px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <span style={{ fontSize:'10px', color:'#93c5fd' }}>📊 DEMONSTRATION MODE — Car park occupancy data shown is simulated. Live ANPR camera integration is available as an optional add-on.</span>
          <button onClick={()=>{ sessionStorage.setItem('lemai_carpark_banner_dismissed','1'); setBannerDismissed(true); }} style={{ background:'none', border:'none', color:'#93c5fd', cursor:'pointer', fontSize:'14px', padding:'0 4px', lineHeight:1 }}>×</button>
        </div>
      )}
      {editing && <CustomizerPanel widgets={CARPARK_WIDGETS} slots={slots} toggle={cpToggle} moveUp={cpMoveUp} moveDown={cpMoveDown} reset={cpReset} onClose={()=>setEditing(false)} />}

      {/* ── Non-demo onboarding banner ── */}
      {!isDemoOrg && (
        <div style={{ marginBottom:'20px', padding:'20px 24px', background:'rgba(59,130,246,0.06)', border:'1px solid rgba(59,130,246,0.2)', borderRadius:'5px' }}>
          <div style={{ display:'flex', alignItems:'flex-start', gap:'16px' }}>
            <div style={{ fontSize:'32px', flexShrink:0, lineHeight:1 }}>🅿️</div>
            <div style={{ flex:1 }}>
              <div style={{ fontWeight:700, fontSize:'15px', color:'#e5e5e5', marginBottom:'6px' }}>Connect Your Car Park System</div>
              <div style={{ fontSize:'13px', color:'#a0a0a0', lineHeight:'1.6', marginBottom:'14px' }}>
                Car Park management requires a live connection to your parking hardware. Lemai supports Dahua NVR-based LPR cameras, Milestone VMS vehicle analytics, and manual vehicle counting via connected IP cameras.
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'12px', marginBottom:'16px' }}>
                {[
                  { icon:'📷', title:'LPR Cameras', desc:'Connect Dahua or compatible IP cameras for automatic number plate recognition at entry/exit points.' },
                  { icon:'🎥', title:'Milestone VMS', desc:'Link your Milestone Video Management Server to pull live vehicle analytics and counting data.' },
                  { icon:'🖥️', title:'Manual Counting', desc:'No hardware? Use manual entry/exit buttons once cameras are assigned to parking levels.' },
                ].map(s => (
                  <div key={s.title} style={{ padding:'14px', background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:'4px' }}>
                    <div style={{ fontSize:'22px', marginBottom:'6px' }}>{s.icon}</div>
                    <div style={{ fontSize:'12px', fontWeight:700, color:'#e5e5e5', marginBottom:'4px' }}>{s.title}</div>
                    <div style={{ fontSize:'11px', color:'#555555', lineHeight:'1.5' }}>{s.desc}</div>
                  </div>
                ))}
              </div>
              <div style={{ display:'flex', gap:'10px', alignItems:'center' }}>
                <a href="/settings" style={{ padding:'9px 20px', background:'linear-gradient(135deg,#3b82f6,#1d4ed8)', border:'none', borderRadius:'4px', color:'white', fontSize:'13px', fontWeight:700, textDecoration:'none', display:'inline-block' }}>
                  Configure in Settings →
                </a>
                <span style={{ fontSize:'11px', color:'#555555' }}>Or contact your Lemai support engineer to schedule an on-site integration.</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'12px', marginBottom:'20px' }}>
        {[
          { l:'Total Spaces',  v: String(totalSpaces), c:'#e2e8f0', icon:'🏗️' },
          { l:'Occupied',      v: String(totalOcc),    c: overallPct>90?'#ef4444':overallPct>70?'#f59e0b':'#22c55e', icon:'🚗' },
          { l:'Available',     v: String(available),   c:'#22c55e', icon:'✅' },
          { l:'Utilisation',   v: `${overallPct}%`,    c: overallPct>90?'#ef4444':overallPct>70?'#f59e0b':'#3b82f6', icon:'📊' },
        ].map(s => (
          <div key={s.l} style={{ background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:'5px', padding:'16px' }}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'10px' }}>
              <span style={{ fontSize:'11px', color:'#555555' }}>{s.l}</span>
              <span style={{ fontSize:'18px' }}>{s.icon}</span>
            </div>
            <div style={{ fontSize:'28px', fontWeight:700, color:s.c }}>{s.v}</div>
          </div>
        ))}
      </div>

      {/* Main card */}
      <div style={{ background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:'5px', overflow:'hidden' }}>
        <div style={{ padding:'0 20px', borderBottom:'1px solid #1a1a1a', display:'flex', alignItems:'center' }}>
          {cpWidgets.filter(w=>isCPVisible(w.id)).map(w => {
            const alertSuffix = w.id==='alerts' && overstay.filter(a=>a.status==='active').length > 0 ? ` (${overstay.filter(a=>a.status==='active').length})` : '';
            return <button key={w.id} style={tabStyle(w.id)} onClick={() => setTab(w.id as any)}>{w.icon} {w.label}{alertSuffix}</button>;
          })}
          <CustomizeButton editing={editing} setEditing={setEditing} />
        </div>

        {/* Vehicle Counting Tab */}
        {tab==='counting' && (
          <div style={{ padding:'20px' }}>
            {/* Live total occupancy summary bar */}
            {(() => {
              const lvlOcc  = levels.reduce((a,l) => a + Math.max(0,l.countIn-l.countOut), 0);
              const lvlCap  = levels.reduce((a,l) => a + l.capacity, 0);
              const lvlPct  = lvlCap > 0 ? Math.min(100, Math.round((lvlOcc/lvlCap)*100)) : 0;
              const barColor = lvlPct >= 90 ? '#ef4444' : lvlPct >= 70 ? '#f59e0b' : '#22c55e';
              return (
                <div style={{ background:'#0d0d0d', border:`1px solid ${barColor}30`, borderRadius:'5px', padding:'14px 18px', marginBottom:'16px' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:'16px', marginBottom:'10px', flexWrap:'wrap' }}>
                    <div style={{ fontSize:'12px', fontWeight:700, color:'#e5e5e5', flex:1 }}>Total Occupancy</div>
                    <div style={{ display:'flex', gap:'20px' }}>
                      <div style={{ textAlign:'center' }}>
                        <div style={{ fontSize:'20px', fontWeight:800, color:'#a0a0a0', lineHeight:1 }}>{lvlCap}</div>
                        <div style={{ fontSize:'9px', color:'#555555', marginTop:'2px' }}>Total Spaces</div>
                      </div>
                      <div style={{ textAlign:'center' }}>
                        <div style={{ fontSize:'20px', fontWeight:800, color:barColor, lineHeight:1 }}>{lvlOcc}</div>
                        <div style={{ fontSize:'9px', color:'#555555', marginTop:'2px' }}>Occupied</div>
                      </div>
                      <div style={{ textAlign:'center' }}>
                        <div style={{ fontSize:'20px', fontWeight:800, color:'#22c55e', lineHeight:1 }}>{lvlCap-lvlOcc}</div>
                        <div style={{ fontSize:'9px', color:'#555555', marginTop:'2px' }}>Available</div>
                      </div>
                      <div style={{ textAlign:'center' }}>
                        <div style={{ fontSize:'20px', fontWeight:800, color:barColor, lineHeight:1 }}>{lvlPct}%</div>
                        <div style={{ fontSize:'9px', color:'#555555', marginTop:'2px' }}>Full</div>
                      </div>
                    </div>
                    <div style={{ fontSize:'10px', fontWeight:700, padding:'3px 10px', borderRadius:'4px', background:`${barColor}18`, color:barColor, flexShrink:0 }}>
                      {lvlPct >= 90 ? 'CRITICAL — NEARLY FULL' : lvlPct >= 70 ? 'HIGH DEMAND' : 'NORMAL'}
                    </div>
                  </div>
                  <div style={{ height:'10px', background:'rgba(255,255,255,0.07)', borderRadius:'5px', overflow:'hidden' }}>
                    <div style={{ height:'100%', width:`${lvlPct}%`, background:barColor, borderRadius:'5px', transition:'width 0.5s' }} />
                  </div>
                </div>
              );
            })()}
            {/* Total summary strip */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'10px', marginBottom:'20px' }}>
              {(() => {
                const totalOcc2 = levels.reduce((a,l) => a + Math.max(0,l.countIn-l.countOut), 0);
                const totalCap  = levels.reduce((a,l) => a + l.capacity, 0);
                const totalIn   = levels.reduce((a,l) => a + l.countIn, 0);
                const totalOut  = levels.reduce((a,l) => a + l.countOut, 0);
                const pct2 = Math.round((totalOcc2/totalCap)*100);
                return [
                  { label:'Total Occupied',  value:totalOcc2, color:pct2>90?'#ef4444':pct2>70?'#f59e0b':'#22c55e' },
                  { label:'Total Available', value:totalCap-totalOcc2, color:'#22c55e' },
                  { label:'Entries Today',   value:totalIn,  color:'#3b82f6' },
                  { label:'Exits Today',     value:totalOut, color:'#a855f7' },
                ].map(s => (
                  <div key={s.label} style={{ background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:'5px', padding:'14px', textAlign:'center' }}>
                    <div style={{ fontSize:'26px', fontWeight:700, color:s.color, lineHeight:1 }}>{s.value}</div>
                    <div style={{ fontSize:'11px', color:'#555555', marginTop:'5px' }}>{s.label}</div>
                  </div>
                ));
              })()}
            </div>

            {/* Level cards */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:'14px', marginBottom:'20px' }}>
              {initialLoading ? Array.from({length:4}).map((_,i) => (
                <div key={i} style={{ background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:'5px', padding:'18px' }}>
                  <div className="skeleton" style={{ height:'13px', width:'80px', marginBottom:'12px' }} />
                  <div className="skeleton" style={{ height:'40px', width:'60%', borderRadius:'4px', marginBottom:'12px' }} />
                  <div className="skeleton" style={{ height:'8px', width:'100%', borderRadius:'4px', marginBottom:'8px' }} />
                  <div style={{ display:'flex', gap:'8px', marginTop:'10px' }}>
                    <div className="skeleton" style={{ height:'32px', flex:1, borderRadius:'4px' }} />
                    <div className="skeleton" style={{ height:'32px', flex:1, borderRadius:'4px' }} />
                  </div>
                </div>
              )) : levels.map(level => (
                <LevelCounterCard key={level.id} level={level} onEntry={handleEntry} onExit={handleExit} onReset={handleReset} />
              ))}
            </div>

            {/* Recent count events */}
            <div style={{ background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:'5px', overflow:'hidden' }}>
              <div style={{ padding:'12px 16px', borderBottom:'1px solid #1a1a1a', fontSize:'12px', fontWeight:600, color:'#a0a0a0', display:'flex', alignItems:'center', gap:'10px' }}>
                <span>Count Event Log</span>
                <div style={{ flex:1 }} />
                <span style={{ fontSize:'11px', color:'#555555', fontWeight:400 }}>Camera detections + manual adjustments</span>
              </div>
              {initialLoading ? Array.from({length:5}).map((_,i) => (
                <div key={i} style={{ display:'flex', alignItems:'center', gap:'12px', padding:'10px 16px', borderBottom:'1px solid rgba(255,255,255,0.04)' }}>
                  <div className="skeleton" style={{ width:'16px', height:'16px', borderRadius:'50%' }} />
                  <div className="skeleton" style={{ height:'12px', width:'100px' }} />
                  <div className="skeleton" style={{ height:'12px', width:'60px', marginLeft:'auto' }} />
                  <div className="skeleton" style={{ height:'11px', width:'55px' }} />
                </div>
              )) : countEvents.slice(0, 12).map(e => (
                <div key={e.id} style={{ display:'flex', alignItems:'center', gap:'12px', padding:'10px 16px', borderBottom:'1px solid rgba(255,255,255,0.04)' }}>
                  <div style={{ fontSize:'16px' }}>{e.direction === 'entry' ? '🟢' : '🔵'}</div>
                  <div style={{ flex:1 }}>
                    <span style={{ fontSize:'12px', fontWeight:600, color:'white' }}>{e.levelName}</span>
                    <span style={{ fontSize:'12px', color:e.direction==='entry'?'#22c55e':'#3b82f6', marginLeft:'10px', fontWeight:600 }}>{e.direction.toUpperCase()}</span>
                  </div>
                  <div style={{ fontSize:'10px', color:'#555555' }}>{e.source === 'manual' ? '👤 Manual' : '📹 Camera'}</div>
                  <div style={{ fontSize:'11px', color:'#444444', fontVariantNumeric:'tabular-nums' }}>{e.time}</div>
                </div>
              ))}
            </div>

            {/* Integration info */}
            <div style={{ marginTop:'14px', padding:'14px 16px', background:'rgba(59,130,246,0.05)', border:'1px solid rgba(59,130,246,0.15)', borderRadius:'5px', fontSize:'12px', color:'#555555' }}>
              💡 <strong style={{ color:'#93c5fd' }}>How it works:</strong> Each level has a dedicated entry ramp camera and exit ramp camera. When a vehicle crosses the detection line, the camera fires an event — entry adds to the count, exit subtracts. The counter shows real-time occupancy. Connect your Dahua NVR in the <strong style={{ color:'#93c5fd' }}>Dahua Integration</strong> module and map your ramp cameras to each level in the camera IDs above.
            </div>
          </div>
        )}

        {/* Analytics & Reports Tab */}
        {tab==='analytics' && (
          <div style={{ padding:'20px' }}>
            {showEmail && (
              <EmailReportModal period={period} levelFilter={levelFilter} typeFilter={typeFilter} onClose={() => setShowEmail(false)} />
            )}

            {/* Controls row */}
            <div style={{ display:'flex', gap:'10px', marginBottom:'20px', flexWrap:'wrap', alignItems:'center' }}>
              {/* Period selector */}
              <div style={{ display:'flex', gap:'4px', background:'#111111', borderRadius:'4px', padding:'4px' }}>
                {(['day','week','month','year'] as ReportPeriod[]).map(p => (
                  <button key={p} onClick={() => setPeriod(p)} style={{ padding:'7px 16px', borderRadius:'4px', border:'none', cursor:'pointer', fontWeight:600, fontSize:'12px', background: period===p?'#3b82f6':'transparent', color: period===p?'white':'#555555' }}>
                    {PERIOD_LABELS[p]}
                  </button>
                ))}
              </div>

              {/* Level filter */}
              <select value={levelFilter} onChange={e => setLevelFilter(e.target.value as ReportLevel)} style={{ background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'4px', padding:'8px 12px', color:'white', fontSize:'12px' }}>
                <option value="all">All Levels</option>
                <option value="L-B2">Level B2</option>
                <option value="L-B1">Level B1</option>
                <option value="L-G">Ground</option>
                <option value="L-R">Roof (P6)</option>
              </select>

              {/* Type filter */}
              <select value={typeFilter} onChange={e => setTypeFilter(e.target.value as ReportType)} style={{ background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'4px', padding:'8px 12px', color:'white', fontSize:'12px' }}>
                <option value="all">All Vehicle Types</option>
                <option value="staff">Staff</option>
                <option value="visitor">Visitor</option>
                <option value="accessible">Accessible</option>
                <option value="ev">EV</option>
              </select>

              <div style={{ flex:1 }} />
              <button onClick={() => setShowEmail(true)} style={{ padding:'9px 20px', background:'linear-gradient(135deg,#3b82f6,#1d4ed8)', border:'none', borderRadius:'4px', color:'white', fontWeight:700, fontSize:'13px', cursor:'pointer' }}>
                📧 Email Report
              </button>
            </div>

            {/* KPI summary row */}
            {(() => {
              const s = PERIOD_SUMMARY[period];
              const rev = REVENUE_DATA[period];
              return (
                <div style={{ display:'grid', gridTemplateColumns:'repeat(6,1fr)', gap:'12px', marginBottom:'22px' }}>
                  {[
                    { label:'Total Entries',    value:s.entries.toLocaleString(),         color:'#3b82f6', icon:'↓' },
                    { label:'Total Exits',      value:s.exits.toLocaleString(),           color:'#22c55e', icon:'↑' },
                    { label:'Peak Occupancy',   value:String(s.peak),                     color:'#ef4444', icon:'⬆' },
                    { label:'Avg Stay Duration',value:s.avgDuration,                      color:'#f59e0b', icon:'⏱' },
                    { label:'Unique Vehicles',  value:s.uniqueVehicles.toLocaleString(),  color:'#a855f7', icon:'🚗' },
                    { label:'Est. Revenue',     value:rev.gross,                          color:'#22d3ee', icon:'💰' },
                  ].map(k => (
                    <div key={k.label} style={{ background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:'5px', padding:'16px' }}>
                      <div style={{ fontSize:'22px', marginBottom:'4px' }}>{k.icon}</div>
                      <div style={{ fontSize:'24px', fontWeight:700, color:k.color, lineHeight:1 }}>{k.value}</div>
                      <div style={{ fontSize:'11px', color:'#555555', marginTop:'5px' }}>{k.label}</div>
                    </div>
                  ))}
                </div>
              );
            })()}

            {/* Main chart */}
            <div style={{ background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:'5px', padding:'20px', marginBottom:'16px' }}>
              <div style={{ display:'flex', alignItems:'center', gap:'12px', marginBottom:'16px' }}>
                <div style={{ fontWeight:700, fontSize:'14px', color:'white', flex:1 }}>
                  Entries — {PERIOD_LABELS[period]}
                  {levelFilter !== 'all' && <span style={{ fontWeight:400, color:'#555555', fontSize:'12px' }}> · {levelFilter}</span>}
                  {typeFilter !== 'all' && <span style={{ fontWeight:400, color:'#555555', fontSize:'12px' }}> · {typeFilter}</span>}
                </div>
                <div style={{ display:'flex', gap:'12px', fontSize:'11px' }}>
                  <span style={{ color:'#3b82f6' }}>■ Entries</span>
                  <span style={{ color:'#22c55e' }}>■ Exits</span>
                </div>
              </div>
              {/* Dual bar chart */}
              {(() => {
                const data = ANALYTICS_DATA[period];
                const show = data.length > 20 ? data.filter((_, i) => i % 3 === 0) : data;
                const maxVal = Math.max(...data.map(d => Math.max(d.entries, d.exits)), 1);
                return (
                  <div>
                    <div style={{ display:'flex', alignItems:'flex-end', gap:'3px', height:'120px' }}>
                      {show.map((d, i) => (
                        <div key={i} style={{ flex:1, display:'flex', gap:'1px', alignItems:'flex-end' }}>
                          <div style={{ flex:1, background:'rgba(59,130,246,0.8)', borderRadius:'2px 2px 0 0', height:`${Math.max(2, Math.round((d.entries / maxVal) * 112))}px` }} title={`Entries: ${d.entries}`} />
                          <div style={{ flex:1, background:'rgba(34,197,94,0.7)', borderRadius:'2px 2px 0 0', height:`${Math.max(2, Math.round((d.exits / maxVal) * 112))}px` }} title={`Exits: ${d.exits}`} />
                        </div>
                      ))}
                    </div>
                    {/* X axis labels */}
                    <div style={{ display:'flex', gap:'3px', marginTop:'6px' }}>
                      {show.map((d, i) => (
                        <div key={i} style={{ flex:1, fontSize:'8px', color:'#444444', textAlign:'center', overflow:'hidden' }}>{d.label}</div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Level breakdown table */}
            <div style={{ background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:'5px', overflow:'hidden', marginBottom:'16px' }}>
              <div style={{ padding:'14px 18px', borderBottom:'1px solid #1a1a1a', fontWeight:700, fontSize:'13px', color:'white' }}>
                Level Breakdown — {PERIOD_LABELS[period]}
              </div>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead>
                  <tr style={{ borderBottom:'1px solid #1a1a1a' }}>
                    {['Level','Capacity','Entries','Exits','Peak Occ.','Utilisation','Trend'].map(h => (
                      <th key={h} style={{ padding:'9px 16px', textAlign:'left', fontSize:'10px', fontWeight:700, color:'#555555', textTransform:'uppercase', letterSpacing:'0.05em' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const s = PERIOD_SUMMARY[period];
                    return [
                      { name:'Level B2',  cap:120, entryPct:0.38, exitPct:0.37, peak:46 },
                      { name:'Level B1',  cap:80,  entryPct:0.31, exitPct:0.31, peak:38 },
                      { name:'Ground',    cap:40,  entryPct:0.16, exitPct:0.17, peak:22 },
                      { name:'Roof (P6)', cap:200, entryPct:0.15, exitPct:0.15, peak:8  },
                    ].map((r, i) => {
                      const entries = Math.round(s.entries * r.entryPct);
                      const exits   = Math.round(s.exits   * r.exitPct);
                      const util    = Math.round((r.peak / r.cap) * 100);
                      const color   = util >= 90 ? '#ef4444' : util >= 70 ? '#f59e0b' : '#22c55e';
                      return (
                        <tr key={r.name} style={{ borderBottom:'1px solid rgba(255,255,255,0.04)' }}
                          onMouseOver={e => (e.currentTarget.style.background='rgba(255,255,255,0.02)') }
                          onMouseOut={e  => (e.currentTarget.style.background='transparent')}>
                          <td style={{ padding:'10px 16px', fontSize:'13px', fontWeight:600, color:'white' }}>{r.name}</td>
                          <td style={{ padding:'10px 16px', fontSize:'12px', color:'#555555' }}>{r.cap}</td>
                          <td style={{ padding:'10px 16px', fontSize:'12px', color:'#3b82f6', fontWeight:600 }}>{entries.toLocaleString()}</td>
                          <td style={{ padding:'10px 16px', fontSize:'12px', color:'#22c55e', fontWeight:600 }}>{exits.toLocaleString()}</td>
                          <td style={{ padding:'10px 16px', fontSize:'12px', color:'#ef4444', fontWeight:700 }}>{r.peak}</td>
                          <td style={{ padding:'10px 16px' }}>
                            <div style={{ display:'flex', alignItems:'center', gap:'6px' }}>
                              <div style={{ width:'60px', height:'5px', background:'rgba(255,255,255,0.07)', borderRadius:'3px' }}>
                                <div style={{ height:'100%', width:`${util}%`, background:color, borderRadius:'3px' }} />
                              </div>
                              <span style={{ fontSize:'11px', color:color, fontWeight:600 }}>{util}%</span>
                            </div>
                          </td>
                          <td style={{ padding:'10px 16px' }}>
                            <MiniBarChart data={ANALYTICS_DATA[period].slice(0, 7)} color={['#3b82f6','#22c55e','#a855f7','#f59e0b'][i]} />
                          </td>
                        </tr>
                      );
                    });
                  })()}
                </tbody>
              </table>
            </div>

            {/* Busiest periods */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px' }}>
              {/* Busiest hours */}
              <div style={{ background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:'5px', padding:'16px' }}>
                <div style={{ fontWeight:700, fontSize:'13px', color:'white', marginBottom:'12px' }}>Peak Hours</div>
                {[
                  { label:'08:00 – 09:00', value:71, pct:100 },
                  { label:'12:00 – 13:00', value:64, pct:90  },
                  { label:'09:00 – 10:00', value:60, pct:85  },
                  { label:'17:00 – 18:00', value:54, pct:76  },
                  { label:'15:00 – 16:00', value:54, pct:76  },
                ].map(h => (
                  <div key={h.label} style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'8px' }}>
                    <div style={{ fontSize:'11px', color:'#555555', width:'110px', flexShrink:0 }}>{h.label}</div>
                    <div style={{ flex:1, height:'6px', background:'rgba(255,255,255,0.07)', borderRadius:'3px' }}>
                      <div style={{ height:'100%', width:`${h.pct}%`, background:'#3b82f6', borderRadius:'3px' }} />
                    </div>
                    <div style={{ fontSize:'11px', color:'#93c5fd', fontWeight:700, width:'30px', textAlign:'right' }}>{h.value}</div>
                  </div>
                ))}
              </div>

              {/* Busiest days of week */}
              <div style={{ background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:'5px', padding:'16px' }}>
                <div style={{ fontWeight:700, fontSize:'13px', color:'white', marginBottom:'12px' }}>Busiest Days of Week</div>
                {makeWeeklyBuckets().sort((a,b) => b.entries - a.entries).map((d, i) => (
                  <div key={d.label} style={{ display:'flex', alignItems:'center', gap:'10px', marginBottom:'8px' }}>
                    <div style={{ fontSize:'11px', color:'#555555', width:'36px', flexShrink:0 }}>{d.label}</div>
                    <div style={{ flex:1, height:'6px', background:'rgba(255,255,255,0.07)', borderRadius:'3px' }}>
                      <div style={{ height:'100%', width:`${Math.round((d.entries/355)*100)}%`, background: i===0?'#ef4444':i===1?'#f59e0b':'#3b82f6', borderRadius:'3px' }} />
                    </div>
                    <div style={{ fontSize:'11px', color:'#a0a0a0', fontWeight:700, width:'36px', textAlign:'right' }}>{d.entries}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Occupancy Heatmap */}
            <div style={{ marginTop:'16px' }}>
              <OccupancyHeatmap />
            </div>

            {/* Dwell time + Revenue side-by-side */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'14px', marginTop:'16px' }}>
              <DwellChart data={DWELL_DATA[period]} />
              {/* Revenue breakdown */}
              <div style={{ background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:'5px', padding:'20px' }}>
                <div style={{ fontWeight:700, fontSize:'13px', color:'white', marginBottom:'2px' }}>Revenue Estimate</div>
                <div style={{ fontSize:'11px', color:'#555555', marginBottom:'14px' }}>Visitor bays @ $4/hr · based on occupancy × avg duration</div>
                {(() => {
                  const rev = REVENUE_DATA[period];
                  const s   = PERIOD_SUMMARY[period];
                  return (
                    <>
                      <div style={{ textAlign:'center', marginBottom:'16px' }}>
                        <div style={{ fontSize:'40px', fontWeight:800, color:'#22d3ee', lineHeight:1 }}>{rev.gross}</div>
                        <div style={{ fontSize:'11px', color:'#555555', marginTop:'6px' }}>{PERIOD_LABELS[period]} gross revenue estimate</div>
                      </div>
                      <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
                        {[
                          { label:'Visitor Exits',    value:rev.visitorExits.toLocaleString(), color:'#a855f7' },
                          { label:'Avg Stay',         value:s.avgDuration,                     color:'#f59e0b' },
                          { label:'Rate/hr',          value:'$4.00',                           color:'#22d3ee' },
                          { label:'Revenue/Vehicle',  value:`$${(rev.visitorExits > 0 ? (parseFloat(rev.gross.replace(/[$,]/g,'')) / rev.visitorExits).toFixed(2) : '0.00')}`, color:'#22c55e' },
                        ].map(r => (
                          <div key={r.label} style={{ display:'flex', justifyContent:'space-between', padding:'8px 12px', background:'#0d0d0d', borderRadius:'4px' }}>
                            <span style={{ fontSize:'12px', color:'#555555' }}>{r.label}</span>
                            <span style={{ fontSize:'12px', fontWeight:700, color:r.color }}>{r.value}</span>
                          </div>
                        ))}
                      </div>
                      <div style={{ marginTop:'12px', fontSize:'10px', color:'#444444', lineHeight:1.5 }}>
                        * Estimate only. Excludes staff, accessible, and EV bays. Actual revenue depends on payment system integration.
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>

            {/* Scheduled Reports */}
            <div style={{ marginTop:'16px' }}>
              <ScheduledReportPanel />
            </div>
          </div>
        )}

        {/* Overstay Alerts Tab */}
        {tab==='alerts' && (
          <div style={{ padding:'20px' }}>
            {/* Config bar */}
            <div style={{ display:'flex', gap:'12px', alignItems:'center', marginBottom:'20px', flexWrap:'wrap' }}>
              <div style={{ flex:1, display:'flex', gap:'8px', alignItems:'center' }}>
                <span style={{ fontSize:'12px', color:'#555555', fontWeight:600 }}>Alert after</span>
                <select value={overstayLimit} onChange={e => setOverstayLimit(Number(e.target.value))} style={{ background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'4px', padding:'7px 10px', color:'white', fontSize:'12px' }}>
                  {[1,2,3,4,6,8,12].map(h => <option key={h} value={h}>{h} hour{h>1?'s':''}</option>)}
                </select>
                <span style={{ fontSize:'12px', color:'#555555' }}>in a visitor bay</span>
              </div>
              <div style={{ display:'flex', gap:'8px', fontSize:'12px' }}>
                <span style={{ padding:'4px 10px', background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.2)', borderRadius:'6px', color:'#ef4444', fontWeight:600 }}>🔴 {overstay.filter(a=>a.status==='active'&&a.severity==='critical').length} Critical</span>
                <span style={{ padding:'4px 10px', background:'rgba(245,158,11,0.1)', border:'1px solid rgba(245,158,11,0.2)', borderRadius:'6px', color:'#f59e0b', fontWeight:600 }}>🟡 {overstay.filter(a=>a.status==='active'&&a.severity==='warning').length} Warning</span>
                <span style={{ padding:'4px 10px', background:'rgba(34,197,94,0.1)', border:'1px solid rgba(34,197,94,0.2)', borderRadius:'6px', color:'#22c55e', fontWeight:600 }}>✓ {overstay.filter(a=>a.status==='cleared').length} Cleared</span>
              </div>
            </div>

            {/* Alert cards */}
            <div style={{ display:'flex', flexDirection:'column', gap:'10px' }}>
              {overstay.map(alert => (
                <div key={alert.id} style={{ background:'#0d0d0d', border:`1px solid ${alert.status==='cleared'?'rgba(255,255,255,0.06)':alert.severity==='critical'?'rgba(239,68,68,0.25)':'rgba(245,158,11,0.2)'}`, borderRadius:'5px', overflow:'hidden', opacity: alert.status==='cleared'?0.55:1 }}>
                  <div style={{ height:'3px', background: alert.status==='cleared'?'#22c55e':alert.severity==='critical'?'#ef4444':'#f59e0b' }} />
                  <div style={{ padding:'14px 16px', display:'flex', gap:'14px', alignItems:'flex-start' }}>
                    <div style={{ fontSize:'28px' }}>{alert.status==='cleared'?'✅':alert.severity==='critical'?'🚨':'⚠️'}</div>
                    <div style={{ flex:1 }}>
                      <div style={{ display:'flex', gap:'10px', alignItems:'center', marginBottom:'6px', flexWrap:'wrap' }}>
                        <span style={{ fontSize:'15px', fontWeight:800, color:'white', fontFamily:'monospace' }}>{alert.plate}</span>
                        <span style={{ fontSize:'10px', padding:'2px 8px', borderRadius:'6px', fontWeight:700,
                          background: alert.severity==='critical'?'rgba(239,68,68,0.12)':'rgba(245,158,11,0.12)',
                          color: alert.severity==='critical'?'#ef4444':'#f59e0b' }}>
                          {alert.severity.toUpperCase()}
                        </span>
                        <span style={{ fontSize:'10px', padding:'2px 8px', borderRadius:'6px', background:'rgba(255,255,255,0.06)', color:'#a0a0a0', fontWeight:600 }}>
                          {alert.status === 'active' ? '🔴 Active' : alert.status === 'noted' ? '📝 Noted' : '✓ Cleared'}
                        </span>
                      </div>
                      <div style={{ display:'flex', gap:'20px', fontSize:'12px', color:'#555555', marginBottom:'6px', flexWrap:'wrap' }}>
                        <span>📍 {alert.zone} — {alert.level}</span>
                        <span>🕐 Entry: {alert.entryTime}</span>
                        <span>⏱ Duration: <strong style={{ color:'white' }}>{alert.duration}</strong></span>
                        <span>Limit: {alert.limitHrs}hr</span>
                        {alert.overstayBy !== '—' && <span style={{ color: alert.severity==='critical'?'#ef4444':'#f59e0b', fontWeight:700 }}>Overstay: +{alert.overstayBy}</span>}
                      </div>
                      {alert.note && <div style={{ fontSize:'11px', color:'#555555', background:'#111111', padding:'6px 10px', borderRadius:'6px', marginTop:'4px' }}>📝 {alert.note}</div>}
                    </div>
                    {alert.status === 'active' && (
                      <div style={{ display:'flex', gap:'6px', flexShrink:0 }}>
                        <button onClick={() => setOverstay(prev => prev.map(a => a.id===alert.id ? { ...a, status:'noted' as const, note:'Operator noted' } : a))}
                          style={{ padding:'7px 14px', borderRadius:'4px', border:'1px solid rgba(59,130,246,0.3)', background:'rgba(59,130,246,0.1)', color:'#93c5fd', fontSize:'11px', fontWeight:600, cursor:'pointer' }}>
                          📝 Note
                        </button>
                        <button onClick={() => setOverstay(prev => prev.map(a => a.id===alert.id ? { ...a, status:'cleared' as const } : a))}
                          style={{ padding:'7px 14px', borderRadius:'4px', border:'1px solid rgba(34,197,94,0.3)', background:'rgba(34,197,94,0.1)', color:'#22c55e', fontSize:'11px', fontWeight:600, cursor:'pointer' }}>
                          ✓ Clear
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div style={{ marginTop:'16px', padding:'12px 16px', background:'rgba(59,130,246,0.05)', border:'1px solid rgba(59,130,246,0.15)', borderRadius:'4px', fontSize:'12px', color:'#555555' }}>
              💡 <strong style={{ color:'#93c5fd' }}>How it works:</strong> Overstay alerts are triggered when a vehicle exceeds the set time limit without an exit event from the camera counter. In a live integration, alerts would be generated automatically from LPR data and can trigger intercom announcements or infringement notices.
            </div>
          </div>
        )}

        {/* Overview Tab */}
        {tab==='overview' && (
          <div style={{ padding:'20px' }}>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:'12px' }}>
              {ZONES.map(z => {
                const pct = Math.round((z.occupied/z.total)*100);
                const avail = z.total - z.occupied;
                const c = pct>90?'#ef4444':pct>70?'#f59e0b':'#22c55e';
                return (
                  <div key={z.id} style={{ background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:'4px', padding:'14px' }}>
                    <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:'10px' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                        <div style={{ width:'32px', height:'32px', borderRadius:'4px', background:`${ZONE_COLOR[z.type]}18`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'16px' }}>{ZONE_ICON[z.type]}</div>
                        <div>
                          <div style={{ fontSize:'12px', fontWeight:600, color:'#e5e5e5' }}>{z.name}</div>
                          <div style={{ fontSize:'9px', color:'#555555' }}>{z.level} · {z.rate}</div>
                        </div>
                      </div>
                      <span style={{ fontSize:'9px', padding:'2px 7px', borderRadius:'7px', fontWeight:700,
                        background: z.status==='full'?'rgba(239,68,68,0.1)':z.status==='near-full'?'rgba(245,158,11,0.1)':'rgba(34,197,94,0.1)',
                        color: z.status==='full'?'#ef4444':z.status==='near-full'?'#f59e0b':'#22c55e',
                      }}>{z.status}</span>
                    </div>

                    {/* Occupancy bar */}
                    <div style={{ marginBottom:'8px' }}>
                      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'4px' }}>
                        <span style={{ fontSize:'10px', color:'#555555' }}>{z.occupied} occupied</span>
                        <span style={{ fontSize:'10px', color:c, fontWeight:600 }}>{pct}%</span>
                      </div>
                      <div style={{ height:'6px', background:'rgba(255,255,255,0.07)', borderRadius:'3px', overflow:'hidden' }}>
                        <div style={{ height:'100%', width:`${pct}%`, background:c, borderRadius:'3px', transition:'width 0.5s' }}/>
                      </div>
                    </div>

                    <div style={{ display:'flex', justifyContent:'space-between' }}>
                      <div style={{ textAlign:'center' }}>
                        <div style={{ fontSize:'14px', fontWeight:700, color:c }}>{avail}</div>
                        <div style={{ fontSize:'8px', color:'#555555' }}>Available</div>
                      </div>
                      <div style={{ textAlign:'center' }}>
                        <div style={{ fontSize:'14px', fontWeight:700, color:'#555555' }}>{z.reserved}</div>
                        <div style={{ fontSize:'8px', color:'#555555' }}>Reserved</div>
                      </div>
                      <div style={{ textAlign:'center' }}>
                        <div style={{ fontSize:'14px', fontWeight:700, color:'#a0a0a0' }}>{z.total}</div>
                        <div style={{ fontSize:'8px', color:'#555555' }}>Total</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Vehicle AI Tab */}
        {tab==='vehicle-ai' && (
          <div style={{ padding:'20px' }}>
            {/* Summary stats */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:12, marginBottom:20 }}>
              {[
                { l:'Total Detections', v: vehicleDetections.length, c:'#f1f5f9' },
                { l:'Cars', v: vehicleDetections.filter(d=>d.type==='car').length, c:'#3b82f6' },
                { l:'Trucks / Vans', v: vehicleDetections.filter(d=>d.type==='truck'||d.type==='van').length, c:'#f59e0b' },
                { l:'Avg. Dwell', v: `${Math.round(vehicleDetections.reduce((s,d)=>s+(d.dwellMinutes||0),0)/vehicleDetections.length)}m`, c:'#8b5cf6' },
                { l:'Plates Captured', v: vehicleDetections.filter(d=>d.plate).length, c:'#22c55e' },
              ].map(s => (
                <div key={s.l} style={{ background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:10, padding:'14px 16px' }}>
                  <div style={{ fontSize:11, color:'#555555', fontWeight:700, textTransform:'uppercase', letterSpacing:0.8, marginBottom:4 }}>{s.l}</div>
                  <div style={{ fontSize:26, fontWeight:700, color:s.c }}>{s.v}</div>
                </div>
              ))}
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:16 }}>
              {/* Vehicle Type Breakdown */}
              <div style={{ background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:10, padding:16 }}>
                <div style={{ fontSize:11, color:'#555555', fontWeight:700, textTransform:'uppercase', letterSpacing:1, marginBottom:14 }}>Vehicle Type Classification</div>
                {(['car','van','truck','motorcycle','bus','bicycle'] as VehicleDetection['type'][]).map(type => {
                  const count = vehicleDetections.filter(d => d.type === type).length;
                  const max = vehicleDetections.filter(d=>d.type==='car').length || 1;
                  const icons: Record<string,string> = { car:'🚗', van:'🚐', truck:'🚛', motorcycle:'🏍️', bus:'🚌', bicycle:'🚲' };
                  return (
                    <div key={type} style={{ marginBottom:10 }}>
                      <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, color:'#a0a0a0', marginBottom:3 }}>
                        <span>{icons[type]} {VEHICLE_TYPE_LABELS[type]}</span>
                        <span style={{ color:'#f1f5f9', fontWeight:600 }}>{count}</span>
                      </div>
                      <div style={{ height:6, background:'rgba(255,255,255,0.06)', borderRadius:3, overflow:'hidden' }}>
                        <div style={{ height:'100%', width:`${(count/max)*100}%`, background:'#3b82f6', borderRadius:3 }}/>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Vehicle Color Breakdown */}
              <div style={{ background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:10, padding:16 }}>
                <div style={{ fontSize:11, color:'#555555', fontWeight:700, textTransform:'uppercase', letterSpacing:1, marginBottom:14 }}>Vehicle Color Distribution</div>
                {(() => {
                  const colorMap: Record<string,{count:number;hex:string}> = {
                    White:{count:0,hex:'#f0f0f0'}, Black:{count:0,hex:'#1a1a1a'}, Silver:{count:0,hex:'#c0c0c0'},
                    Blue:{count:0,hex:'#2563eb'}, Red:{count:0,hex:'#dc2626'}, Grey:{count:0,hex:'#6b7280'},
                    Green:{count:0,hex:'#16a34a'},
                  };
                  vehicleDetections.forEach(d => { if (colorMap[d.color]) colorMap[d.color].count++; });
                  const max = Math.max(...Object.values(colorMap).map(v=>v.count), 1);
                  return Object.entries(colorMap).sort((a,b)=>b[1].count-a[1].count).map(([color,{count,hex}]) => (
                    <div key={color} style={{ marginBottom:10 }}>
                      <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, color:'#a0a0a0', marginBottom:3 }}>
                        <span style={{ display:'flex', alignItems:'center', gap:6 }}>
                          <span style={{ width:12, height:12, borderRadius:2, background:hex, border:'1px solid rgba(255,255,255,0.15)', display:'inline-block' }}/>
                          {color}
                        </span>
                        <span style={{ color:'#f1f5f9', fontWeight:600 }}>{count}</span>
                      </div>
                      <div style={{ height:6, background:'rgba(255,255,255,0.06)', borderRadius:3, overflow:'hidden' }}>
                        <div style={{ height:'100%', width:`${(count/max)*100}%`, background:hex==='#f0f0f0'?'#94a3b8':hex, borderRadius:3 }}/>
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </div>

            {/* Entry / Exit direction split */}
            <div style={{ background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:10, padding:16, marginBottom:16 }}>
              <div style={{ fontSize:11, color:'#555555', fontWeight:700, textTransform:'uppercase', letterSpacing:1, marginBottom:12 }}>Entry / Exit Distribution</div>
              <div style={{ display:'flex', gap:12 }}>
                {(['entering','exiting','unknown'] as VehicleDetection['direction'][]).map(dir => {
                  const count = vehicleDetections.filter(d=>d.direction===dir).length;
                  const pct = Math.round((count/vehicleDetections.length)*100);
                  const colors: Record<string,string> = { entering:'#22c55e', exiting:'#ef4444', unknown:'#555555' };
                  return (
                    <div key={dir} style={{ flex:1, background:'#0d0d0d', borderRadius:8, padding:'12px 16px', textAlign:'center' }}>
                      <div style={{ fontSize:24, fontWeight:700, color:colors[dir] }}>{count}</div>
                      <div style={{ fontSize:11, color:'#555555', marginTop:4, textTransform:'capitalize' }}>{dir} ({pct}%)</div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Detection Log */}
            <div style={{ background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:10, padding:16 }}>
              <div style={{ fontSize:11, color:'#555555', fontWeight:700, textTransform:'uppercase', letterSpacing:1, marginBottom:12 }}>AI Detection Log</div>
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                  <thead>
                    <tr style={{ borderBottom:'1px solid rgba(255,255,255,0.08)' }}>
                      {['Time','Camera','Type','Color','Direction','Plate','Dwell','Confidence'].map(h => (
                        <th key={h} style={{ padding:'6px 10px', textAlign:'left', color:'#555555', fontWeight:600, fontSize:11, textTransform:'uppercase', letterSpacing:0.5 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {vehicleDetections.map(d => {
                      const icons: Record<string,string> = { car:'🚗', van:'🚐', truck:'🚛', motorcycle:'🏍️', bus:'🚌', bicycle:'🚲' };
                      const colorHexes: Record<string,string> = { White:'#f0f0f0', Black:'#1a1a1a', Silver:'#c0c0c0', Blue:'#2563eb', Red:'#dc2626', Grey:'#6b7280', Green:'#16a34a' };
                      return (
                        <tr key={d.id} style={{ borderBottom:'1px solid rgba(255,255,255,0.04)' }}>
                          <td style={{ padding:'8px 10px', color:'#a0a0a0' }}>{d.timestamp}</td>
                          <td style={{ padding:'8px 10px', color:'#e5e5e5' }}>{d.camera}</td>
                          <td style={{ padding:'8px 10px', color:'#e5e5e5' }}>{icons[d.type]} {VEHICLE_TYPE_LABELS[d.type]}</td>
                          <td style={{ padding:'8px 10px' }}>
                            <span style={{ display:'flex', alignItems:'center', gap:6 }}>
                              <span style={{ width:12, height:12, borderRadius:2, background:colorHexes[d.color]||'#555555', border:'1px solid rgba(255,255,255,0.2)', flexShrink:0 }}/>
                              <span style={{ color:'#e5e5e5' }}>{d.color}</span>
                            </span>
                          </td>
                          <td style={{ padding:'8px 10px' }}>
                            <span style={{ fontSize:11, padding:'2px 7px', borderRadius:3, background: d.direction==='entering'?'rgba(34,197,94,0.1)':d.direction==='exiting'?'rgba(239,68,68,0.1)':'rgba(255,255,255,0.05)', color: d.direction==='entering'?'#4ade80':d.direction==='exiting'?'#fca5a5':'#555555' }}>
                              {d.direction==='entering'?'↓ Entry':d.direction==='exiting'?'↑ Exit':'—'}
                            </span>
                          </td>
                          <td style={{ padding:'8px 10px', color: d.plate?'#f1f5f9':'#222222', fontFamily:'monospace', fontSize:11 }}>{d.plate || '—'}</td>
                          <td style={{ padding:'8px 10px', color:'#a0a0a0' }}>{d.dwellMinutes != null ? `${d.dwellMinutes}m` : '—'}</td>
                          <td style={{ padding:'8px 10px', color:'#22c55e' }}>{d.confidence}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Events Tab */}
        {tab==='events' && (
          <div>
            <div style={{ padding:'10px 20px', borderBottom:'1px solid rgba(255,255,255,0.04)', fontSize:'11px', color:'#555555' }}>
              Live LPR events · Auto-refresh active
            </div>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead>
                <tr style={{ borderBottom:'1px solid #1a1a1a' }}>
                  {['Plate','Type','Zone','Action','Time','Duration'].map(h => (
                    <th key={h} style={{ padding:'9px 20px', textAlign:'left', fontSize:'10px', fontWeight:700, color:'#555555', textTransform:'uppercase', letterSpacing:'0.06em' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {EVENTS.map(e => (
                  <tr key={e.id} style={{ borderBottom:'1px solid rgba(255,255,255,0.04)' }}
                    onMouseOver={ev => (ev.currentTarget.style.background='rgba(255,255,255,0.02)')}
                    onMouseOut={ev => (ev.currentTarget.style.background='transparent')}
                  >
                    <td style={{ padding:'10px 20px', fontSize:'12px', color:'#e5e5e5', fontFamily:'monospace', fontWeight:600 }}>{e.plate}</td>
                    <td style={{ padding:'10px 20px' }}>
                      <span style={{ fontSize:'10px', padding:'2px 8px', borderRadius:'4px', fontWeight:600, background:'rgba(255,255,255,0.05)', color:'#a0a0a0' }}>{e.type}</span>
                    </td>
                    <td style={{ padding:'10px 20px', fontSize:'12px', color:'#a0a0a0' }}>{e.zone}</td>
                    <td style={{ padding:'10px 20px' }}>
                      <span style={{ fontSize:'10px', padding:'2px 8px', borderRadius:'4px', fontWeight:700,
                        background: e.action==='entry'?'rgba(34,197,94,0.1)':'rgba(59,130,246,0.1)',
                        color: e.action==='entry'?'#22c55e':'#3b82f6',
                      }}>{e.action}</span>
                    </td>
                    <td style={{ padding:'10px 20px', fontSize:'11px', color:'#555555', fontVariantNumeric:'tabular-nums' }}>{e.time}</td>
                    <td style={{ padding:'10px 20px', fontSize:'11px', color:'#555555' }}>{e.duration||'—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Zones Tab */}
        {tab==='zones' && (
          <div style={{ padding:'20px' }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead>
                <tr style={{ borderBottom:'1px solid #1a1a1a' }}>
                  {['Zone','Level','Type','Total','Occupied','Available','Utilisation','Rate'].map(h => (
                    <th key={h} style={{ padding:'9px 16px', textAlign:'left', fontSize:'10px', fontWeight:700, color:'#555555', textTransform:'uppercase', letterSpacing:'0.05em' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ZONES.map(z => {
                  const pct = Math.round((z.occupied/z.total)*100);
                  const c = pct>90?'#ef4444':pct>70?'#f59e0b':'#22c55e';
                  return (
                    <tr key={z.id} style={{ borderBottom:'1px solid rgba(255,255,255,0.04)' }}
                      onMouseOver={e => (e.currentTarget.style.background='rgba(255,255,255,0.02)')}
                      onMouseOut={e => (e.currentTarget.style.background='transparent')}
                    >
                      <td style={{ padding:'10px 16px', fontSize:'12px', color:'#e5e5e5', fontWeight:500 }}>{z.name}</td>
                      <td style={{ padding:'10px 16px', fontSize:'11px', color:'#555555' }}>{z.level}</td>
                      <td style={{ padding:'10px 16px' }}>
                        <span style={{ fontSize:'9px', padding:'2px 7px', borderRadius:'7px', background:`${ZONE_COLOR[z.type]}18`, color:ZONE_COLOR[z.type], fontWeight:600 }}>{z.type}</span>
                      </td>
                      <td style={{ padding:'10px 16px', fontSize:'12px', color:'#a0a0a0', fontVariantNumeric:'tabular-nums' }}>{z.total}</td>
                      <td style={{ padding:'10px 16px', fontSize:'12px', color:'#a0a0a0', fontVariantNumeric:'tabular-nums' }}>{z.occupied}</td>
                      <td style={{ padding:'10px 16px', fontSize:'12px', color:c, fontWeight:600, fontVariantNumeric:'tabular-nums' }}>{z.total-z.occupied}</td>
                      <td style={{ padding:'10px 16px' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:'6px' }}>
                          <div style={{ flex:1, height:'4px', background:'rgba(255,255,255,0.07)', borderRadius:'2px', minWidth:'60px' }}>
                            <div style={{ height:'100%', width:`${pct}%`, background:c, borderRadius:'2px' }}/>
                          </div>
                          <span style={{ fontSize:'10px', color:c, fontWeight:600, minWidth:'32px' }}>{pct}%</span>
                        </div>
                      </td>
                      <td style={{ padding:'10px 16px', fontSize:'10px', color:'#555555' }}>{z.rate}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Bookings Tab */}
        {tab==='bookings' && (
          <div style={{ padding:'20px' }}>
            {/* Stats row */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'12px', marginBottom:'20px' }}>
              {[
                { label:'Active',    value: (isDemoOrg?SEED_BOOKINGS:[]).filter(b=>b.status==='active').length,    color:'#22c55e' },
                { label:'Confirmed', value: (isDemoOrg?SEED_BOOKINGS:[]).filter(b=>b.status==='confirmed').length, color:'#3b82f6' },
                { label:'Today',     value: (isDemoOrg?SEED_BOOKINGS:[]).filter(b=>b.date==='Today').length,       color:'#f59e0b' },
                { label:'Tomorrow',  value: (isDemoOrg?SEED_BOOKINGS:[]).filter(b=>b.date==='Tomorrow').length,    color:'#a855f7' },
              ].map(s => (
                <div key={s.label} style={{ background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:'5px', padding:'16px', textAlign:'center' }}>
                  <div style={{ fontSize:'28px', fontWeight:700, color:s.color }}>{s.value}</div>
                  <div style={{ fontSize:'10px', color:'#555555', marginTop:'4px', textTransform:'uppercase', letterSpacing:'0.05em' }}>{s.label}</div>
                </div>
              ))}
            </div>

            {/* Action bar */}
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'16px' }}>
              <div style={{ display:'flex', gap:'6px' }}>
                {(['All','Today','Tomorrow','Active','Confirmed','Completed'] as const).map(f => (
                  <button key={f} style={{ padding:'5px 12px', borderRadius:'4px', border:'none', cursor:'pointer', fontSize:'11px', fontWeight:600, background:'rgba(255,255,255,0.06)', color:'#a0a0a0' }}>{f}</button>
                ))}
              </div>
              <button style={{ padding:'7px 16px', borderRadius:'4px', border:'none', cursor:'pointer', background:'#3b82f6', color:'white', fontSize:'12px', fontWeight:600 }}>+ New Booking</button>
            </div>

            {/* Bookings table */}
            <div style={{ background:'#0d0d0d', border:'1px solid #1a1a1a', borderRadius:'5px', overflow:'hidden' }}>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead>
                  <tr style={{ borderBottom:'1px solid #1a1a1a' }}>
                    {['Ref','Name','Plate','Bay','Level','Date','Time In','Time Out','Type','Status',''].map(h => (
                      <th key={h} style={{ padding:'9px 14px', textAlign:'left', fontSize:'10px', fontWeight:700, color:'#555555', textTransform:'uppercase', letterSpacing:'0.05em', whiteSpace:'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(isDemoOrg?SEED_BOOKINGS:[]).map(b => (
                    <tr key={b.id} style={{ borderBottom:'1px solid rgba(255,255,255,0.04)' }}
                      onMouseOver={e => (e.currentTarget.style.background='rgba(255,255,255,0.02)')}
                      onMouseOut={e => (e.currentTarget.style.background='transparent')}
                    >
                      <td style={{ padding:'10px 14px', fontSize:'11px', color:'#555555', fontVariantNumeric:'tabular-nums' }}>{b.ref}</td>
                      <td style={{ padding:'10px 14px', fontSize:'12px', color:'#e5e5e5', fontWeight:500 }}>{b.name}</td>
                      <td style={{ padding:'10px 14px', fontSize:'11px', color:'#a0a0a0', fontFamily:'monospace' }}>{b.plate}</td>
                      <td style={{ padding:'10px 14px', fontSize:'12px', color:'white', fontWeight:700 }}>{b.bay}</td>
                      <td style={{ padding:'10px 14px', fontSize:'11px', color:'#555555' }}>{b.level}</td>
                      <td style={{ padding:'10px 14px', fontSize:'11px', color:'#a0a0a0' }}>{b.date}</td>
                      <td style={{ padding:'10px 14px', fontSize:'11px', color:'#a0a0a0', fontVariantNumeric:'tabular-nums' }}>{b.timeIn}</td>
                      <td style={{ padding:'10px 14px', fontSize:'11px', color:'#a0a0a0', fontVariantNumeric:'tabular-nums' }}>{b.timeOut}</td>
                      <td style={{ padding:'10px 14px' }}>
                        <span style={{ fontSize:'9px', padding:'2px 7px', borderRadius:'7px', background:`${BOOKING_TYPE_COLOR[b.type]}18`, color:BOOKING_TYPE_COLOR[b.type], fontWeight:600, textTransform:'uppercase' }}>{b.type}</span>
                      </td>
                      <td style={{ padding:'10px 14px' }}>
                        <span style={{ fontSize:'9px', padding:'2px 8px', borderRadius:'7px', background:`${BOOKING_STATUS_COLOR[b.status]}18`, color:BOOKING_STATUS_COLOR[b.status], fontWeight:700, textTransform:'uppercase' }}>{b.status}</span>
                      </td>
                      <td style={{ padding:'10px 14px' }}>
                        <div style={{ display:'flex', gap:'4px' }}>
                          <button style={{ padding:'3px 8px', borderRadius:'6px', border:'none', cursor:'pointer', background:'rgba(59,130,246,0.15)', color:'#3b82f6', fontSize:'10px', fontWeight:600 }}>Edit</button>
                          {b.status === 'confirmed' && (
                            <button style={{ padding:'3px 8px', borderRadius:'6px', border:'none', cursor:'pointer', background:'rgba(239,68,68,0.12)', color:'#ef4444', fontSize:'10px', fontWeight:600 }}>Cancel</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
